import {
  User as UserIcon, RefreshCw, Mail,
  ShieldCheck, Zap, Edit3, Save, X,
  History, Plus, Trash2, Key, Loader2,
  CheckCircle2, Info, ChevronRight, SendHorizontal,
  DatabaseZap, Volume2, ShieldPlus, Terminal, Layout,
  Eye, EyeOff, Filter, Search, Activity, Cpu, ArrowRightLeft,
  Settings2, FlaskConical, Clipboard, Clock, Sliders, Calendar,
  AlertTriangle, Link, Fingerprint, Anchor, ExternalLink,
  Target, TrendingUp, DollarSign, Users, MousePointer2,
  Server, AlertOctagon, BarChart, PieChart, Briefcase,
  ArrowRight, ArrowUpRight, ArrowDownRight, Minus, LineChart,
  Users2, UserCheck, TrendingDown, BookOpen, Scale, FileText,
  Clock3, FileClock, Globe, Share2, ShieldAlert,
  Power, PowerOff, Book, Code, Box, Lock, Megaphone,
  CheckCircle, Play, AlertCircle, Hash, Code2, ClipboardCheck,
  UserX, HardDrive, Network, Layers, TrendingUp as GrowthIcon,
  Shield, Coins, Fingerprint as RetentionIcon,
  CreditCard, ShieldEllipsis, Wifi, WifiOff, Radio, ArrowUp, ArrowDown,
  Bell, Smartphone, MailCheck, RefreshCcw, MousePointerClick,
  BarChart2, Signal, Stethoscope, MessageSquare
} from 'lucide-react';
import React, { useState, useEffect, useMemo } from 'react';
import {
  User, UserRole, UserTier, EmailLog, MessageTemplate, TriggerAssociation,
  SystemEvent, Broadcast, BroadcastGroup, HopTrace, AnalyticsMetrics,
  Quadrant, TrustDocument, TrustVersion, TrustAuditLog,
  Actor, TrustCategory, SystemConfig, PostmarkConfig, DeliveryChannel, AudienceFilter,
  Feedback, FeedbackType, FeedbackStatus
} from '../../types';
import { firebaseService, db, getAuthHeaders } from '../../services/firebaseService';
import { API_BASE } from '../../apiConfig';
import { communicationService } from '../../services/communicationService';
import { analyticsService } from '../../services/analyticsService';
import { notificationService } from '../../services/notificationService';
import { TEMPLATE_VARIABLES, ORCHESTRATORS, EVENT_REQUIRED_CONTEXT } from '../../services/templateContextRegistry';
import { ENV, CORE_TEMPLATES, CORE_ASSOCIATIONS } from '../../constants';
import { SocialLinksTab } from './SocialLinksTab';
import { useActionFlow } from '../ActionModal';

export const IntelPage: React.FC = () => {
  const { startAction, openAlert } = useActionFlow();
  const [users, setUsers] = useState<User[]>([]);
  const [tasks, setTasks] = useState<any[]>([]);
  const [templates, setTemplates] = useState<MessageTemplate[]>([]);
  const [associations, setAssociations] = useState<TriggerAssociation[]>([]);
  const [emailLogs, setEmailLogs] = useState<EmailLog[]>([]);
  const [broadcasts, setBroadcasts] = useState<Broadcast[]>([]);
  const [trustDocuments, setTrustDocuments] = useState<TrustDocument[]>([]);
  const [systemConfig, setSystemConfig] = useState<SystemConfig | null>(null);
  const [postmarkConfig, setPostmarkConfig] = useState<PostmarkConfig | null>(null);

  const [activeTab, setActiveTab] = useState<'intelligence' | 'users' | 'messaging' | 'social_links' | 'trust_cms' | 'ledger' | 'feedback' | 'recovery'>('intelligence');
  const [messagingSubTab, setMessagingSubTab] = useState<'logs' | 'broadcasts' | 'templates' | 'associations' | 'library'>('logs');
  const [loadingTemplateId, setLoadingTemplateId] = useState<string | null>(null);

  const [isRefreshing, setIsRefreshing] = useState(false);
  const [relaySearch, setRelaySearch] = useState('');
  const [userSearch, setUserSearch] = useState('');
  const [templateSearch, setTemplateSearch] = useState('');
  const [triggerSearch, setTriggerSearch] = useState('');
  const [broadcastSearch, setBroadcastSearch] = useState('');
  const [showSecretKeys, setShowSecretKeys] = useState(false);
  const [stripeConnection, setStripeConnection] = useState<{ status: 'idle' | 'testing' | 'success' | 'error'; mode?: string; accountName?: string; error?: string }>({ status: 'idle' });
  const [sortConfig, setSortConfig] = useState<{ key: keyof User; direction: 'asc' | 'desc' } | null>(null);
  const [feedbackItems, setFeedbackItems] = useState<Feedback[]>([]);
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [feedbackTypeFilter, setFeedbackTypeFilter] = useState<FeedbackType | 'ALL'>('ALL');
  const [feedbackStatusFilter, setFeedbackStatusFilter] = useState<FeedbackStatus | 'ALL'>('ALL');

  // Dashboard State
  const [timeRange, setTimeRange] = useState<'7d' | '30d' | '90d'>('30d');
  const [selectedMetric, setSelectedMetric] = useState<string>('growth_new_users');
  const [userSegment, setUserSegment] = useState<'ALL' | 'PREMIUM' | 'FREE'>('ALL');

  // Stripe Configuration Form
  const [stripeForm, setStripeForm] = useState<SystemConfig>({
    stripePublicKey: '',
    stripeSecretKey: '',
    stripeWebhookSecret: ''
  });

  // Promo Code State
  const [promoCodes, setPromoCodes] = useState<any[]>([]);
  const [promoLoading, setPromoLoading] = useState(false);
  const [promoForm, setPromoForm] = useState({ code: '', discountType: 'percent' as 'percent' | 'fixed', discountValue: '', maxRedemptions: '', expiresAt: '' });

  // Postmark Configuration Form
  const [postmarkForm, setPostmarkForm] = useState<PostmarkConfig>({
    serverToken: '',
    fromEmail: ''
  });

  // Relay Test Tool
  const [relayTestStatus, setRelayTestStatus] = useState<'IDLE' | 'TESTING' | 'PASS' | 'FAIL'>('IDLE');
  const [relayTestResponse, setRelayTestResponse] = useState<string | null>(null);

  // Diagnostic Audit
  const [diagnosticResult, setDiagnosticResult] = useState<any>(null);
  const [isRunningDiag, setIsRunningDiag] = useState(false);

  // REACTIVE SELECTION: Tracks by ID to allow live updates in the modal
  const [selectedLogId, setSelectedLogId] = useState<string | null>(null);
  const activeTraceLog = useMemo(() => emailLogs.find(l => l.id === selectedLogId), [emailLogs, selectedLogId]);

  // Broadcast State
  const [pulseForm, setPulseForm] = useState({
    title: '',
    message: '',
    targetGroup: 'ALL' as BroadcastGroup,
    priority: 'NORMAL' as 'NORMAL' | 'CRITICAL',
    ctaLabel: '',
    ctaUrl: ''
  });

  // New Broadcast Segmentation State
  const [deliveryChannels, setDeliveryChannels] = useState<DeliveryChannel[]>(['IN_APP']);
  const [audienceFilter, setAudienceFilter] = useState<AudienceFilter>({ type: 'ALL', days: 7 });
  const [protocolStatus, setProtocolStatus] = useState<'IDLE' | 'SYNCING' | 'SYNCED' | 'DRIFT'>('IDLE');

  // Template Editor State
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [templateEditForm, setTemplateEditForm] = useState<Partial<MessageTemplate>>({});
  const activeEditTemplate = useMemo(() => templates.find(t => t.id === selectedTemplateId), [templates, selectedTemplateId]);

  const [newAssoc, setNewAssoc] = useState({ systemEvent: '' as SystemEvent, emailTask: '', schedule: 'realtime' as 'realtime' | 'daily' | 'weekly', deliveryMode: 'per_user' as 'per_user' | 'broadcast' });

  const hubMetrics = useMemo(() => analyticsService.calculateMetrics(tasks, users), [tasks, users]);

  // --- 4-PILLAR INTELLIGENCE CALCULATION WITH HARDENED DEFINITIONS ---
  const pillarMetrics = useMemo(() => {
    const now = Date.now();
    const rangeDays = timeRange === '7d' ? 7 : timeRange === '30d' ? 30 : 90;
    const rangeMs = rangeDays * 86400000;
    const prevRangeMs = rangeMs * 2;

    // Helper: Filter by date range
    const inRange = (ts: number) => ts >= (now - rangeMs) && ts <= now;
    const inPrevRange = (ts: number) => ts >= (now - prevRangeMs) && ts < (now - rangeMs);

    // Filter Users by Segment
    const segmentUsers = users.filter(u => userSegment === 'ALL' || u.tier === (userSegment === 'PREMIUM' ? UserTier.PREMIUM : UserTier.FREE));

    // --- PILLAR 1: GROWTH ---
    const newUsers = segmentUsers.filter(u => inRange(u.createdAt));
    const prevNewUsers = segmentUsers.filter(u => inPrevRange(u.createdAt));
    const growthDelta = prevNewUsers.length > 0
      ? Math.round(((newUsers.length - prevNewUsers.length) / prevNewUsers.length) * 100)
      : 0;

    // Activation: New users in range who have completed at least 1 task
    const activatedUsers = newUsers.filter(u => tasks.some(t => t.userId === u.id && (t.isCompleted || t.status === 'COMPLETED')));
    const activationRate = newUsers.length > 0 ? Math.round((activatedUsers.length / newUsers.length) * 100) : 0;

    // --- PILLAR 2: RETENTION ---
    // Active Users in current range (Login activity)
    const activeInRange = segmentUsers.filter(u => inRange(u.lastLogin));
    const activeInPrevRange = segmentUsers.filter(u => inPrevRange(u.lastLogin));
    const retentionDelta = activeInPrevRange.length > 0
      ? Math.round(((activeInRange.length - activeInPrevRange.length) / activeInPrevRange.length) * 100)
      : 0;

    // At Risk: Inactive > 7 days (Snapshot of current state, not historical)
    const atRiskUsers = segmentUsers.filter(u => (now - u.lastLogin) > (7 * 86400000));

    // --- PILLAR 3: MONETIZATION ---
    // MRR Proxy: Premium users * $10 (Strict Assumption Labeling required in UI)
    const currentPremium = segmentUsers.filter(u => u.tier === UserTier.PREMIUM);
    const prevPremium = segmentUsers.filter(u => u.tier === UserTier.PREMIUM && u.createdAt < (now - rangeMs));
    // Accurate Delta: New premium users in range vs prev range
    const newPremiumInRange = currentPremium.filter(u => inRange(u.createdAt));
    const newPremiumInPrev = currentPremium.filter(u => inPrevRange(u.createdAt));

    const mrr = currentPremium.length * 10;
    const mrrDelta = newPremiumInPrev.length > 0
      ? Math.round(((newPremiumInRange.length - newPremiumInPrev.length) / newPremiumInPrev.length) * 100)
      : 0;

    const conversionRate = segmentUsers.length > 0 ? ((currentPremium.length / segmentUsers.length) * 100).toFixed(1) : "0.0";

    // --- PILLAR 4: STABILITY (Hardened Definition) ---
    const logsInRange = emailLogs.filter(l => inRange(l.timestamp));
    const logsInPrev = emailLogs.filter(l => inPrevRange(l.timestamp));

    const failsInRange = logsInRange.filter(l => l.status === 'FAILED');
    const failsInPrev = logsInPrev.filter(l => l.status === 'FAILED');

    // Error Rate: Only calculated if attempts exist to avoid NaN or misleading 0s
    const errorRateVal = logsInRange.length > 0 ? (failsInRange.length / logsInRange.length) * 100 : 0;
    const prevErrorRate = logsInPrev.length > 0 ? (failsInPrev.length / logsInPrev.length) * 100 : 0;

    const errorDelta = prevErrorRate > 0 ? Math.round(((errorRateVal - prevErrorRate) / prevErrorRate) * 100) : 0;

    // "Active Incidents" -> Renamed to "Failed Relay Events" to be strictly accurate to data source
    const failedEvents = failsInRange.length;
    const stabilityDataAvailable = logsInRange.length > 0;

    // --- TREND GENERATOR (Real Bucketed Data) ---
    const generateTrend = (data: any[], dateKey: string, filterFn?: (item: any) => boolean) => {
      const points = [];
      const buckets = 12; // 12 points for smoother sparkline
      const bucketSize = rangeMs / buckets;

      if (data.length === 0) return Array(buckets).fill(0);

      for (let i = 0; i < buckets; i++) {
        const start = (now - rangeMs) + (i * bucketSize);
        const end = start + bucketSize;
        points.push(data.filter(item => {
          const ts = item[dateKey];
          const isInBucket = ts >= start && ts < end;
          return filterFn ? isInBucket && filterFn(item) : isInBucket;
        }).length);
      }
      return points;
    };

    // --- EXECUTIVE SUMMARY ENGINE ---
    let systemStatus: 'HEALTHY' | 'WARNING' | 'CRITICAL' = 'HEALTHY';
    let summaryText = "Systems operational. Baseline metrics established.";

    // Severity Rules (Priority Order: Stability > Growth > Retention > Monetization)
    if (failedEvents > 5 || errorRateVal > 10) {
      systemStatus = 'CRITICAL';
      summaryText = `CRITICAL RELAY ALERT: ${failedEvents} failed relay events detected with ${errorRateVal.toFixed(1)}% error rate. Immediate relay remediation required.`;
    } else if (errorRateVal > 2) {
      systemStatus = 'WARNING';
      summaryText = `Stability degradation detected. Error rate elevated at ${errorRateVal.toFixed(1)}%. Monitor outgoing relay protocols.`;
    } else if (!stabilityDataAvailable && timeRange === '7d') {
      // Special case: No logs in 7 days could mean broken logging or low volume
      summaryText = "Insufficient telemetry for stability analysis. No relay events recorded in window.";
    } else if (growthDelta < -15) {
      systemStatus = 'WARNING';
      summaryText = `Growth velocity has decelerated by ${Math.abs(growthDelta)}% period-over-period. Review acquisition funnels.`;
    } else if (retentionDelta < -10) {
      systemStatus = 'WARNING';
      summaryText = `User retention slipping (${retentionDelta}%). ${atRiskUsers.length} users currently at risk of churn.`;
    } else if (growthDelta > 15) {
      systemStatus = 'HEALTHY';
      summaryText = `Strong growth trajectory verified (+${growthDelta}%). New user acquisition is outpacing churn.`;
    } else if (mrrDelta > 0) {
      systemStatus = 'HEALTHY';
      summaryText = `Monetization efficiency stable. Premium conversions tracking positive.`;
    }

    // Explicit Data Availability Flag
    const hasEnoughData = users.length > 0 || tasks.length > 0 || emailLogs.length > 0;

    if (!hasEnoughData) {
      console.warn("[Intel] Insufficient data for reliable metrics. System defaults applied.");
      summaryText = "Insufficient data for executive analysis. Awaiting telemetry.";
    }

    return {
      growth: {
        newUsers: newUsers.length,
        delta: growthDelta,
        activationRate,
        trend: generateTrend(segmentUsers, 'createdAt'),
        recentSignups: newUsers.sort((a, b) => b.createdAt - a.createdAt).slice(0, 5),
        hasData: segmentUsers.length > 0
      },
      retention: {
        retentionRate: retentionDelta,
        activeUsers: activeInRange.length,
        atRiskCount: atRiskUsers.length,
        delta: retentionDelta,
        trend: generateTrend(segmentUsers, 'lastLogin'),
        atRiskUsers: atRiskUsers.sort((a, b) => a.lastLogin - b.lastLogin).slice(0, 5),
        hasData: segmentUsers.length > 0
      },
      monetization: {
        mrr,
        conversionRate,
        premiumCount: currentPremium.length,
        trend: generateTrend(currentPremium, 'createdAt'),
        mrrDelta,
        hasData: segmentUsers.length > 0
      },
      stability: {
        failureRate: stabilityDataAvailable ? errorRateVal.toFixed(2) : "N/A",
        failedEvents, // Strictly defined as failed log count
        delta: errorDelta,
        trend: generateTrend(emailLogs, 'timestamp', l => l.status === 'FAILED'),
        recentErrors: failsInRange.sort((a, b) => b.timestamp - a.timestamp).slice(0, 5),
        hasData: stabilityDataAvailable
      },
      summary: {
        status: systemStatus,
        text: summaryText
      }
    };
  }, [users, tasks, emailLogs, timeRange, userSegment]);


  const sortedUsers = useMemo(() => {
    let sortableUsers = [...users];
    if (sortConfig !== null) {
      sortableUsers.sort((a, b) => {
        // @ts-ignore
        const aValue = a[sortConfig.key] ?? 0;
        // @ts-ignore
        const bValue = b[sortConfig.key] ?? 0;

        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableUsers.filter(u =>
      u.email.toLowerCase().includes(userSearch.toLowerCase()) ||
      u.displayName.toLowerCase().includes(userSearch.toLowerCase())
    );
  }, [users, sortConfig, userSearch]);

  const targetedUsers = useMemo(() => {
    let base = users;
    if (pulseForm.targetGroup === 'PREMIUM') base = base.filter(u => u.tier === UserTier.PREMIUM);
    if (pulseForm.targetGroup === 'FREE') base = base.filter(u => u.tier === UserTier.FREE);
    if (pulseForm.targetGroup === 'ADMINS') base = base.filter(u => u.role === UserRole.ADMIN);

    const now = Date.now();
    const dayMs = 24 * 60 * 60 * 1000;
    const days = audienceFilter.days || 7;

    if (audienceFilter.type === 'ACTIVE_RECENTLY') {
      base = base.filter(u => (now - (u.lastLogin || 0)) < (days * dayMs));
    } else if (audienceFilter.type === 'NEW_USERS') {
      base = base.filter(u => (now - u.createdAt) < (days * dayMs));
    } else if (audienceFilter.type === 'NEVER_LOGGED_IN') {
      base = base.filter(u => !u.lastLogin || (u.lastLogin - u.createdAt) < (5 * 60 * 1000));
    }
    return base;
  }, [users, pulseForm.targetGroup, audienceFilter]);

  const filteredTemplates = useMemo(() => {
    return templates.filter(t =>
      t.name.toLowerCase().includes(templateSearch.toLowerCase()) ||
      t.subject.toLowerCase().includes(templateSearch.toLowerCase())
    );
  }, [templates, templateSearch]);

  const filteredAssociations = useMemo(() => {
    return associations.filter(a =>
      a.systemEvent.toLowerCase().includes(triggerSearch.toLowerCase()) ||
      a.emailTask.toLowerCase().includes(triggerSearch.toLowerCase())
    );
  }, [associations, triggerSearch]);

  const filteredBroadcasts = useMemo(() => {
    return broadcasts.filter(b =>
      b.title.toLowerCase().includes(broadcastSearch.toLowerCase()) ||
      b.message.toLowerCase().includes(broadcastSearch.toLowerCase())
    );
  }, [broadcasts, broadcastSearch]);

  const requestSort = (key: keyof User) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  useEffect(() => {
    loadData();
    const unsubLogs = firebaseService.subscribeToEmailLogs((logs) => setEmailLogs(logs));
    return () => unsubLogs();
  }, []);

  useEffect(() => { if (activeTab === 'ledger') loadPromoCodes(); }, [activeTab]);
  useEffect(() => { if (activeTab === 'feedback') loadFeedback(); }, [activeTab]);

  const loadFeedback = async () => {
    setFeedbackLoading(true);
    try {
      const items = await firebaseService.getAllFeedback();
      setFeedbackItems(items);
    } catch (e) { console.warn('Failed to load feedback', e); }
    setFeedbackLoading(false);
  };

  const handleUpdateFeedbackStatus = (item: Feedback, newStatus: FeedbackStatus) => {
    const statusLabel = newStatus === 'IN_PROGRESS' ? 'In Progress' : 'Completed';
    startAction({
      title: `Mark as ${statusLabel}`,
      message: `Move "${item.message.substring(0, 60)}${item.message.length > 60 ? '...' : ''}" to ${statusLabel}?`,
      confirmLabel: `Mark ${statusLabel}`,
      variant: 'info',
      action: async () => {
        await firebaseService.updateFeedbackStatus(item.id, newStatus);
        await loadFeedback();
      },
      successMessage: `Feedback moved to ${statusLabel}.`
    });
  };

  const loadData = async () => {
    setIsRefreshing(true);
    try {
      const currentAdmin = JSON.parse(localStorage.getItem('priori_user_session') || '{}');
      const [fUsers, fLogs, fAssocs, fTemplates, fTasks, fTrustDocs, fBroadcasts, fConfig, fPostmark] = await Promise.all([
        firebaseService.getUsers(),
        firebaseService.getEmailLogs(),
        firebaseService.getAssociations(),
        firebaseService.getTemplates(),
        firebaseService.getTasks('all_admin_override'),
        firebaseService.getTrustDocuments(),
        firebaseService.getBroadcasts(currentAdmin),
        firebaseService.getSystemConfig(),
        firebaseService.getPostmarkConfig()
      ]);
      setUsers(fUsers);
      setTemplates(fTemplates);
      setEmailLogs(fLogs);
      setAssociations(fAssocs);
      setTasks(fTasks || []);
      setTrustDocuments(fTrustDocs);
      setBroadcasts(fBroadcasts);

      if (fConfig) {
        setSystemConfig(fConfig);
        // Mask secret keys — only show last 4 chars in the form
        const maskKey = (key: string) => key ? '••••••••' + key.slice(-4) : '';
        setStripeForm({
          ...fConfig,
          stripeSecretKey: maskKey(fConfig.stripeSecretKey),
          stripeWebhookSecret: maskKey(fConfig.stripeWebhookSecret || ''),
        });
      }

      if (fPostmark) {
        setPostmarkConfig(fPostmark);
        setPostmarkForm(fPostmark);
      }
    } catch (e) { console.warn("Admin Load Degraded", e); }
    setIsRefreshing(false);
  };

  const handleTogglePremium = async (user: User) => {
    const newTier = user.tier === UserTier.PREMIUM ? UserTier.FREE : UserTier.PREMIUM;
    startAction({
      title: 'Modify User Clearance',
      message: `Change tier for ${user.displayName} from ${user.tier} to ${newTier}? This will affect their feature access immediately.`,
      confirmLabel: 'Update Tier',
      variant: 'warning',
      action: async () => {
        await firebaseService.updateUser(user.id, { tier: newTier });
        await loadData();
      },
      successMessage: 'User tier updated successfully.'
    });
  };

  const handleDeleteUser = async (user: User) => {
    startAction({
      title: 'Decommission Operator',
      message: `Permanently delete user ${user.email}? This action wipes all mission data and cannot be undone.`,
      confirmLabel: 'Delete User',
      variant: 'danger',
      action: async () => {
        await firebaseService.deleteUser(user.id);
        await loadData();
      },
      successMessage: 'Operator profile scrubbed.'
    });
  };

  const handleSaveStripeConfig = async (e: React.FormEvent) => {
    e.preventDefault();
    startAction({
      title: 'Sync Ledger Configuration',
      message: 'Update Stripe API keys? Invalid keys will disrupt subscription processing.',
      confirmLabel: 'Sync Config',
      variant: 'info',
      action: async () => {
        // Only send keys that were actually changed (not masked placeholders)
        const isMasked = (val: string) => !val || val.startsWith('••••••••');
        const configToSave: Record<string, any> = {};
        if (!isMasked(stripeForm.stripePublicKey)) configToSave.stripePublicKey = stripeForm.stripePublicKey;
        if (!isMasked(stripeForm.stripeSecretKey)) configToSave.stripeSecretKey = stripeForm.stripeSecretKey;
        if (!isMasked(stripeForm.stripeWebhookSecret || '')) configToSave.stripeWebhookSecret = stripeForm.stripeWebhookSecret;
        configToSave.monthlyPriceCents = stripeForm.monthlyPriceCents || 600;
        configToSave.yearlyPriceCents = stripeForm.yearlyPriceCents || 5900;
        await firebaseService.updateSystemConfig(configToSave as any);
        await loadData();
        notificationService.sendLocal("Protocol Synchronized", "Financial ledger configuration updated.");
      },
      successMessage: 'Ledger protocols synchronized.'
    });
  };

  const loadPromoCodes = async () => {
    setPromoLoading(true);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_BASE}/api/stripe/promo-codes`, { headers });
      if (res.ok) setPromoCodes(await res.json());
    } catch (e) { console.warn('Failed to load promo codes', e); }
    setPromoLoading(false);
  };

  const handleCreatePromoCode = (e: React.FormEvent) => {
    e.preventDefault();
    startAction({
      title: 'Create Promotion Code',
      message: `Create promo code "${promoForm.code.toUpperCase()}" with ${promoForm.discountType === 'percent' ? promoForm.discountValue + '% off' : '$' + promoForm.discountValue + ' off'}?`,
      confirmLabel: 'Create Code',
      variant: 'info',
      action: async () => {
        const headers = await getAuthHeaders();
        const res = await fetch(`${API_BASE}/api/stripe/promo-codes`, {
          method: 'POST',
          headers,
          body: JSON.stringify(promoForm),
        });
        if (!res.ok) { const err = await res.json(); throw new Error(err.error || 'Failed to create promo code'); }
        setPromoForm({ code: '', discountType: 'percent', discountValue: '', maxRedemptions: '', expiresAt: '' });
        await loadPromoCodes();
        notificationService.sendLocal("Code Created", `Promo code ${promoForm.code.toUpperCase()} is now active.`);
      },
      successMessage: 'Promotion code created and active on Stripe.'
    });
  };

  const handleDeactivatePromoCode = (id: string, code: string) => {
    startAction({
      title: 'Deactivate Promotion Code',
      message: `Deactivate "${code}"? Users will no longer be able to use this code at checkout.`,
      confirmLabel: 'Deactivate',
      variant: 'danger',
      action: async () => {
        const headers = await getAuthHeaders();
        const res = await fetch(`/api/stripe/promo-codes/${id}/deactivate`, { method: 'POST', headers });
        if (!res.ok) { const err = await res.json(); throw new Error(err.error || 'Failed to deactivate'); }
        await loadPromoCodes();
        notificationService.sendLocal("Code Deactivated", `${code} has been deactivated.`);
      },
      successMessage: 'Promotion code deactivated.'
    });
  };

  const handleSavePostmarkConfig = async (e: React.FormEvent) => {
    e.preventDefault();
    startAction({
      title: 'Update Relay Protocol',
      message: 'Modify Postmark server credentials? This may interrupt ongoing email dispatches.',
      confirmLabel: 'Update Relay',
      variant: 'warning',
      action: async () => {
        await firebaseService.updatePostmarkConfig(postmarkForm);
        setPostmarkConfig(postmarkForm);
        notificationService.sendLocal("Relay Shard Updated", "Communication protocol updated.");
      },
      successMessage: 'Relay configuration updated.'
    });
  };

  const handleRunDiagnostic = async () => {
    setIsRunningDiag(true);
    try {
      const result = await communicationService.testPostmarkConnection();
      setDiagnosticResult(result);
    } catch (e: any) {
      setDiagnosticResult({ error: e.message || "Client-side failure." });
    } finally { setIsRunningDiag(false); }
  };

  const runRelayTest = async () => {
    setRelayTestStatus('TESTING');
    setRelayTestResponse(null);
    try {
      const currentAdmin = JSON.parse(localStorage.getItem('priori_user_session') || '{}');
      const result = await communicationService.processEvent({
        to: currentAdmin.email || 'test@priori-ai.com',
        event: 'welcome_new_user',
        context: {
          userName: currentAdmin.displayName || 'System Admin',
          userEmail: currentAdmin.email || 'test@priori-ai.com',
          userId: 'test_execution_node',
          appUrl: window.location.origin,
          timestamp: new Date().toLocaleString()
        }
      });
      if (result.success) {
        setRelayTestStatus('PASS');
        setRelayTestResponse(`ID: ${result.trace.find(t => t.step === 7)?.details || 'Verified'}`);
      } else {
        setRelayTestStatus('FAIL');
        setRelayTestResponse(result.trace[result.trace.length - 1].details);
      }
    } catch (e: any) {
      setRelayTestStatus('FAIL');
      setRelayTestResponse(e.message);
    }
  };

  const synchronizeBroadcastProtocols = async (): Promise<boolean> => {
    setProtocolStatus('SYNCING');
    try {
      const codeTemplate = CORE_TEMPLATES.find(t => t.id === 'BROADCAST_ALERT');
      const codeAssoc = CORE_ASSOCIATIONS.find(a => a.systemEvent === 'broadcast');
      if (!codeTemplate || !codeAssoc) throw new Error("Constants missing.");
      const dbTemplate = await firebaseService.getTemplate(codeTemplate.id);
      if (!dbTemplate) await firebaseService.updateTemplate(codeTemplate.id, codeTemplate);
      const dbAssoc = await firebaseService.getAssociationByEvent('broadcast');
      if (!dbAssoc) await firebaseService.createAssociation(codeAssoc);
      setProtocolStatus('SYNCED');
      return true;
    } catch (e) {
      setProtocolStatus('DRIFT');
      return false;
    }
  };

  const handleDeployPulse = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!pulseForm.title || !pulseForm.message) return;
    if (deliveryChannels.length === 0) {
      openAlert({ title: "Validation Error", message: "Select at least one delivery channel.", variant: 'warning' });
      return;
    }

    startAction({
      title: 'Dispatch Pulse',
      message: `Deploy broadcast "${pulseForm.title}" to ${targetedUsers.length} operators via [${deliveryChannels.join(', ')}]?`,
      confirmLabel: 'Dispatch',
      variant: 'info',
      action: async () => {
        await firebaseService.createBroadcast({
          ...pulseForm,
          timestamp: Date.now(),
          senderName: 'Vanguard Command',
          channels: deliveryChannels,
          audienceFilter: audienceFilter
        });
        if (deliveryChannels.includes('EMAIL')) {
          const syncSuccess = await synchronizeBroadcastProtocols();
          if (syncSuccess || (await firebaseService.getTemplate('BROADCAST_ALERT'))) {
            const recipients = targetedUsers;
            for (let i = 0; i < recipients.length; i += 5) {
              const batch = recipients.slice(i, i + 5);
              await Promise.all(batch.map(u =>
                communicationService.processEvent({
                  to: u.email,
                  event: 'broadcast',
                  context: {
                    userName: u.displayName, userEmail: u.email, userId: u.id,
                    appUrl: window.location.origin, customMessage: pulseForm.message, customTitle: pulseForm.title
                  }
                }).catch(() => { })
              ));
            }
          }
        }
        setPulseForm({ title: '', message: '', targetGroup: 'ALL', priority: 'NORMAL', ctaLabel: '', ctaUrl: '' });
        await loadData();
      },
      successMessage: 'Broadcast dispatched successfully.'
    });
  };

  const handleCreateAssoc = async () => {
    if (!newAssoc.systemEvent || !newAssoc.emailTask) return;
    startAction({
      title: 'Bind Protocol',
      message: `Link event [${newAssoc.systemEvent}] to template [${newAssoc.emailTask}]?`,
      confirmLabel: 'Bind',
      variant: 'info',
      action: async () => {
        const id = Math.random().toString(36).substr(2, 9);
        await firebaseService.createAssociation({ id, ...newAssoc });
        setNewAssoc({ systemEvent: '' as SystemEvent, emailTask: '', schedule: 'realtime', deliveryMode: 'per_user' });
        await loadData();
      },
      successMessage: 'Trigger association active.'
    });
  };

  const handleDeleteAssoc = async (id: string) => {
    startAction({
      title: 'Unbind Protocol',
      message: 'Disconnect this trigger association? Automated relays for this event will cease.',
      confirmLabel: 'Unbind',
      variant: 'warning',
      action: async () => {
        await firebaseService.deleteAssociation(id);
        await loadData();
      },
      successMessage: 'Association removed.'
    });
  };

  const handleSaveTemplate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTemplateId || !templateEditForm.subject || !templateEditForm.body) return;

    startAction({
      title: 'Update Tactical Protocol',
      message: `Permanently modify the ${templateEditForm.name} communication shard? This will affect all future dispatches using this template.`,
      confirmLabel: 'Save Protocol',
      variant: 'warning',
      action: async () => {
        const payload = {
          ...templateEditForm,
          lastModified: Date.now()
        };
        await firebaseService.updateTemplate(selectedTemplateId, payload);
        await loadData();
        setSelectedTemplateId(null);
      },
      successMessage: 'Communication shard updated.'
    });
  };

  const toggleChannel = (channel: DeliveryChannel) => {
    setDeliveryChannels(prev => prev.includes(channel) ? prev.filter(c => c !== channel) : [...prev, channel]);
  };

  const renderTabButton = (id: typeof activeTab, label: string, icon: React.ReactNode) => (
    <button
      onClick={() => setActiveTab(id)}
      className={`px-6 py-4 rounded-2xl flex items-center gap-3 transition-all ${activeTab === id ? 'bg-blue-600 text-white shadow-xl shadow-blue-600/20' : 'bg-white text-slate-500 hover:text-slate-900 hover:bg-slate-50 border border-slate-100'}`}
    >
      {icon}
      <span className="text-[10px] font-black uppercase tracking-widest">{label}</span>
    </button>
  );

  const MetricSparkline = ({ data, colorClass }: { data: number[], colorClass: string }) => {
    const max = Math.max(...data, 1);
    const points = data.map((val, i) => {
      const x = (i / (data.length - 1)) * 100;
      const y = 100 - (val / max) * 100;
      return `${x},${y}`;
    }).join(' ');

    return (
      <div className="h-8 w-24 relative overflow-hidden">
        <svg viewBox="0 0 100 100" className="w-full h-full overflow-visible" preserveAspectRatio="none">
          <path d={`M 0 100 L ${points} L 100 100 Z`} className={`${colorClass.replace('text-', 'fill-')}/20`} stroke="none" />
          <polyline points={points} fill="none" strokeWidth="3" className={colorClass} strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
    );
  };

  const MetricRow = ({ id, label, value, sub, trend, delta, color, active }: any) => (
    <div
      onClick={() => setSelectedMetric(id)}
      className={`flex items-center justify-between p-4 rounded-xl cursor-pointer border transition-all ${active ? 'bg-slate-900 border-slate-900 text-white shadow-lg' : 'bg-white border-slate-100 hover:border-slate-300'}`}
    >
      <div className="space-y-1">
        <span className={`text-[10px] font-black uppercase tracking-widest ${active ? 'text-slate-400' : 'text-slate-500'}`}>{label}</span>
        <div className="flex items-baseline gap-2">
          <span className="text-xl font-black">{value}</span>
          {delta !== undefined && (
            <span className={`flex items-center text-[9px] font-bold ${delta > 0 ? 'text-emerald-500' : delta < 0 ? 'text-rose-500' : 'text-slate-400'}`}>
              {delta > 0 ? <ArrowUpRight size={10} /> : delta < 0 ? <ArrowDownRight size={10} /> : <Minus size={10} />} {Math.abs(delta)}%
            </span>
          )}
        </div>
      </div>
      <div className="text-right">
        {trend && <MetricSparkline data={trend} colorClass={color} />}
      </div>
    </div>
  );

  const PillarCard = ({ title, icon: Icon, color, children, alert }: any) => (
    <div className={`space-y-4 p-1 rounded-2xl transition-all ${alert ? 'animate-pulse' : ''}`}>
      <div className="flex items-center gap-3 px-2">
        <div className={`p-2 rounded-lg ${color.bg} ${color.text}`}>
          <Icon size={16} />
        </div>
        <h3 className={`text-xs font-black uppercase tracking-widest ${alert ? 'text-rose-600' : 'text-slate-500'}`}>{title}</h3>
        {alert && <ShieldAlert size={14} className="text-rose-600" />}
      </div>
      <div className={`space-y-3 ${alert ? 'ring-2 ring-rose-500/20 rounded-2xl p-2 bg-rose-500/5' : ''}`}>
        {children}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 p-6 lg:p-12 pb-32 animate-in fade-in duration-500 relative font-sans">

      {/* TRACE MODAL */}
      {selectedLogId && activeTraceLog && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/60 backdrop-blur-md animate-in fade-in duration-200" onClick={() => setSelectedLogId(null)}>
          <div className="bg-white w-full max-w-3xl rounded-[2.5rem] border border-slate-200 shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300" onClick={e => e.stopPropagation()}>
            <div className="p-8 border-b border-slate-200 bg-white flex justify-between items-center">
              <div className="space-y-1">
                <h3 className="text-xl font-black uppercase tracking-tighter flex items-center gap-3 text-slate-900">
                  <Zap size={20} className={activeTraceLog.status === 'SENT' ? 'text-emerald-500' : 'text-rose-500'} />
                  Execution Trace
                </h3>
                <p className="text-[10px] font-mono text-slate-500 uppercase tracking-widest">ID: {activeTraceLog.id}</p>
              </div>
              <button onClick={() => setSelectedLogId(null)} className="p-2 hover:bg-slate-100 rounded-xl"><X size={20} /></button>
            </div>
            <div className="p-8 max-h-[60vh] overflow-y-auto custom-scrollbar space-y-6 bg-slate-50">
              {!activeTraceLog.metadata?.trace ? (
                <div className="text-center py-10 opacity-50 font-mono text-xs uppercase">No telemetry trace available.</div>
              ) : (
                <div className="space-y-4">
                  {activeTraceLog.metadata.trace.map((hop: HopTrace, idx: number) => (
                    <div key={idx} className="flex gap-4 group">
                      <div className="flex flex-col items-center">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-[10px] border-2 z-10 ${hop.status === 'PASS' ? 'bg-emerald-500 border-emerald-600 text-white' : 'bg-rose-500 border-rose-600 text-white'}`}>
                          {hop.step}
                        </div>
                        {idx < (activeTraceLog.metadata?.trace?.length || 0) - 1 && (
                          <div className={`w-0.5 flex-1 ${hop.status === 'PASS' ? 'bg-emerald-500/20' : 'bg-rose-500/20'}`} />
                        )}
                      </div>
                      <div className="flex-1 pb-6">
                        <div className={`p-4 rounded-2xl border ${hop.status === 'PASS' ? 'bg-white border-slate-200' : 'bg-rose-50 border-rose-500/20'}`}>
                          <div className="flex justify-between items-start mb-2">
                            <h4 className="text-xs font-black uppercase tracking-tight text-slate-900">{hop.name}</h4>
                            <span className="text-[9px] font-mono text-slate-400 opacity-50">{new Date(hop.timestamp).toLocaleTimeString()}</span>
                          </div>
                          <p className="text-[10px] font-mono text-slate-500 leading-relaxed break-all">
                            {hop.details}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}



      {/* Header */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-12 gap-6">
        <div className="space-y-2">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-blue-600/10 text-blue-600 rounded-xl">
              <ShieldCheck size={24} />
            </div>
            <h1 className="text-4xl font-black uppercase tracking-tighter leading-none text-slate-900">Command Hub</h1>
          </div>
          <p className="text-[10px] font-mono text-slate-500 uppercase tracking-[0.3em] font-bold pl-1">Vanguard System Intelligence</p>
        </div>

        <div className="flex items-center gap-4">
          <button
            onClick={loadData}
            disabled={isRefreshing}
            className="p-4 bg-white border border-slate-200 rounded-2xl hover:bg-slate-50 transition-all active:scale-95 disabled:opacity-50"
          >
            <RefreshCw size={20} className={`text-slate-600 ${isRefreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="flex flex-wrap gap-4 mb-10 overflow-x-auto pb-4 custom-scrollbar">
        {renderTabButton('intelligence', 'Intel & Metrics', <DatabaseZap size={16} />)}
        {renderTabButton('users', 'User Registry', <Users size={16} />)}
        {renderTabButton('messaging', 'Orchestration Hub', <SendHorizontal size={16} />)}
        {renderTabButton('social_links', 'Social Nodes', <Share2 size={16} />)}
        {renderTabButton('trust_cms', 'Trust Protocol', <Shield size={16} />)}
        {renderTabButton('ledger', 'Ledger', <Coins size={16} />)}
        {renderTabButton('feedback', 'Feedback', <MessageSquare size={16} />)}
        {renderTabButton('recovery', 'System Recovery', <Server size={16} />)}
      </div>

      {/* INTELLIGENCE DASHBOARD */}
      {activeTab === 'intelligence' && (
        <div className="space-y-8 animate-in slide-in-from-bottom-4">
          {/* Top Filter Bar */}
          <div className="flex flex-wrap items-center gap-4 bg-white p-4 rounded-3xl border border-slate-200 shadow-sm">
            <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-100 rounded-xl">
              <Filter size={14} className="text-slate-500" />
              <span className="text-[10px] font-black uppercase text-slate-500">Filters</span>
            </div>
            <div className="h-8 w-px bg-slate-200" />
            <div className="flex gap-2">
              {(['7d', '30d', '90d'] as const).map(t => (
                <button key={t} onClick={() => setTimeRange(t)} className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-wider transition-all ${timeRange === t ? 'bg-slate-900 text-white' : 'bg-slate-50 text-slate-500 hover:bg-slate-100'}`}>
                  {t}
                </button>
              ))}
            </div>
            <div className="h-8 w-px bg-slate-200" />
            <div className="flex gap-2">
              {(['ALL', 'PREMIUM', 'FREE'] as const).map(s => (
                <button key={s} onClick={() => setUserSegment(s)} className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-wider transition-all ${userSegment === s ? 'bg-indigo-600 text-white' : 'bg-slate-50 text-slate-500 hover:bg-slate-100'}`}>
                  {s}
                </button>
              ))}
            </div>
          </div>

          {/* System Health Strip */}
          <div className="bg-white p-6 rounded-[2rem] border border-slate-200 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div className="flex flex-wrap items-center gap-6">
              <div className="space-y-1">
                <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Relay Node</p>
                <div className="flex items-center gap-2">
                  <div className={`w-2.5 h-2.5 rounded-full ${postmarkConfig ? 'bg-emerald-500 animate-pulse' : 'bg-rose-500'}`} />
                  <span className="text-sm font-bold text-slate-900">{postmarkConfig ? 'Postmark Active' : 'Relay Offline'}</span>
                </div>
              </div>
              <div className="w-px h-8 bg-slate-100 hidden md:block" />
              <div className="space-y-1">
                <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Broadcast Protocol</p>
                <div className="flex items-center gap-2">
                  <div className={`w-2.5 h-2.5 rounded-full ${protocolStatus === 'DRIFT' ? 'bg-amber-500' : 'bg-emerald-500'}`} />
                  <span className="text-sm font-bold text-slate-900">{protocolStatus === 'IDLE' ? 'Synced' : protocolStatus}</span>
                </div>
              </div>
              <div className="w-px h-8 bg-slate-100 hidden md:block" />
              <div className="space-y-1">
                <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">System Latency</p>
                <span className="text-sm font-bold text-slate-900">{hubMetrics.latency}ms <span className="text-emerald-500 text-[10px]">p95</span></span>
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={synchronizeBroadcastProtocols} className="p-3 bg-slate-50 hover:bg-blue-50 text-blue-600 rounded-xl transition-colors border border-slate-200"><RefreshCcw size={16} /></button>
              <button onClick={() => setActiveTab('recovery')} className="p-3 bg-slate-50 hover:bg-slate-100 text-slate-600 rounded-xl transition-colors border border-slate-200"><Stethoscope size={16} /></button>
            </div>
          </div>

          {/* Executive Summary Intelligence Strip */}
          <div className={`p-6 rounded-[2rem] border shadow-sm flex items-center gap-5 transition-all ${pillarMetrics.summary.status === 'CRITICAL' ? 'bg-rose-50 border-rose-200' : pillarMetrics.summary.status === 'WARNING' ? 'bg-amber-50 border-amber-200' : 'bg-white border-slate-200'}`}>
            <div className={`p-4 rounded-2xl shrink-0 ${pillarMetrics.summary.status === 'CRITICAL' ? 'bg-rose-200 text-rose-700' : pillarMetrics.summary.status === 'WARNING' ? 'bg-amber-200 text-amber-700' : 'bg-emerald-100 text-emerald-600'}`}>
              {pillarMetrics.summary.status === 'CRITICAL' ? <ShieldAlert size={24} /> : pillarMetrics.summary.status === 'WARNING' ? <AlertTriangle size={24} /> : <CheckCircle2 size={24} />}
            </div>
            <div className="space-y-1">
              <h3 className={`text-xs font-black uppercase tracking-widest ${pillarMetrics.summary.status === 'CRITICAL' ? 'text-rose-700' : pillarMetrics.summary.status === 'WARNING' ? 'text-amber-700' : 'text-slate-500'}`}>Executive Summary</h3>
              <p className={`text-sm font-bold leading-tight ${pillarMetrics.summary.status === 'CRITICAL' ? 'text-rose-900' : pillarMetrics.summary.status === 'WARNING' ? 'text-amber-900' : 'text-slate-900'}`}>
                {pillarMetrics.summary.text}
              </p>
            </div>
          </div>

          {/* 4-Pillar Grid + Diagnostic Panel */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Column: Intelligence Feed */}
            <div className="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* PILLAR 1: GROWTH */}
              <PillarCard title="Growth Engine" icon={TrendingUp} color={{ bg: 'bg-blue-50', text: 'text-blue-600' }}>
                {pillarMetrics.growth.hasData ? (
                  <>
                    <MetricRow
                      id="growth_new_users"
                      label="New Users"
                      value={pillarMetrics.growth.newUsers}
                      delta={pillarMetrics.growth.delta}
                      trend={pillarMetrics.growth.trend}
                      color="text-blue-500"
                      active={selectedMetric === 'growth_new_users'}
                    />
                    <MetricRow
                      id="growth_activation"
                      label="Activation Rate"
                      value={`${pillarMetrics.growth.activationRate}%`}
                      color="text-blue-500"
                      active={selectedMetric === 'growth_activation'}
                    />
                  </>
                ) : <div className="text-xs text-slate-400 p-4 font-bold uppercase tracking-widest">Insufficient Data</div>}
              </PillarCard>

              {/* PILLAR 2: RETENTION */}
              <PillarCard title="Retention & Health" icon={Fingerprint} color={{ bg: 'bg-emerald-50', text: 'text-emerald-600' }}>
                {pillarMetrics.retention.hasData ? (
                  <>
                    <MetricRow
                      id="retention_rate"
                      label="Active Users"
                      value={pillarMetrics.retention.activeUsers}
                      delta={pillarMetrics.retention.delta}
                      trend={pillarMetrics.retention.trend}
                      color="text-emerald-500"
                      active={selectedMetric === 'retention_rate'}
                    />
                    <MetricRow
                      id="retention_risk"
                      label="At-Risk Users (>7d)"
                      value={pillarMetrics.retention.atRiskCount}
                      color="text-emerald-500"
                      active={selectedMetric === 'retention_risk'}
                    />
                  </>
                ) : <div className="text-xs text-slate-400 p-4 font-bold uppercase tracking-widest">Insufficient Data</div>}
              </PillarCard>

              {/* PILLAR 3: MONETIZATION */}
              <PillarCard title="Monetization" icon={DollarSign} color={{ bg: 'bg-amber-50', text: 'text-amber-600' }}>
                {pillarMetrics.monetization.hasData ? (
                  <>
                    <MetricRow
                      id="monetization_mrr"
                      label="MRR (Est.)"
                      value={`$${pillarMetrics.monetization.mrr}`}
                      trend={pillarMetrics.monetization.trend}
                      delta={pillarMetrics.monetization.mrrDelta}
                      color="text-amber-500"
                      active={selectedMetric === 'monetization_mrr'}
                    />
                    <MetricRow
                      id="monetization_conv"
                      label="Conversion Rate"
                      value={`${pillarMetrics.monetization.conversionRate}%`}
                      sub={`${pillarMetrics.monetization.premiumCount} Premium`}
                      color="text-amber-500"
                      active={selectedMetric === 'monetization_conv'}
                    />
                  </>
                ) : <div className="text-xs text-slate-400 p-4 font-bold uppercase tracking-widest">Insufficient Data</div>}
              </PillarCard>

              {/* PILLAR 4: STABILITY */}
              <PillarCard
                title="System Stability"
                icon={Activity}
                color={{ bg: 'bg-rose-50', text: 'text-rose-600' }}
                alert={pillarMetrics.summary.status === 'CRITICAL'}
              >
                {pillarMetrics.stability.hasData ? (
                  <>
                    <MetricRow
                      id="stability_error"
                      label="Error Rate"
                      value={`${pillarMetrics.stability.failureRate}%`}
                      trend={pillarMetrics.stability.trend}
                      delta={pillarMetrics.stability.delta}
                      color="text-rose-500"
                      active={selectedMetric === 'stability_error'}
                    />
                    <MetricRow
                      id="stability_incident"
                      label="Failed Relay Events"
                      value={pillarMetrics.stability.failedEvents}
                      color="text-rose-500"
                      active={selectedMetric === 'stability_incident'}
                    />
                  </>
                ) : <div className="text-xs text-slate-400 p-4 font-bold uppercase tracking-widest">Insufficient Data</div>}
              </PillarCard>
            </div>

            {/* Right Column: Diagnostic Panel (Sticky) */}
            <div className="lg:col-span-1">
              <div className="sticky top-6 bg-slate-900 text-white p-8 rounded-[2.5rem] shadow-2xl min-h-[600px] flex flex-col relative overflow-hidden">
                <div className="absolute top-0 right-0 w-64 h-64 bg-blue-600/20 blur-[100px] rounded-full pointer-events-none" />

                <div className="flex items-center gap-3 mb-8 relative z-10">
                  <Stethoscope className="text-blue-400" size={24} />
                  <h3 className="text-lg font-black uppercase tracking-tight">Diagnostic</h3>
                </div>

                <div className="flex-1 space-y-8 relative z-10">
                  {/* Context: Growth */}
                  {selectedMetric.startsWith('growth') && (
                    <>
                      <div className="space-y-2">
                        <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">Metric Definition</p>
                        <p className="text-sm font-medium text-slate-300 leading-relaxed">
                          {selectedMetric === 'growth_new_users' ? 'Total unique user registrations within the selected period.' : 'Percentage of new users who completed at least one task.'}
                        </p>
                      </div>
                      <div className="space-y-4">
                        <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">Recent Signups</p>
                        {pillarMetrics.growth.recentSignups.length > 0 ? pillarMetrics.growth.recentSignups.map(u => (
                          <div key={u.id} className="flex items-center gap-3 p-3 rounded-xl bg-white/5 border border-white/10">
                            <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center text-xs font-bold text-blue-400">{u.displayName.charAt(0)}</div>
                            <div>
                              <p className="text-xs font-bold">{u.displayName}</p>
                              <p className="text-[10px] text-slate-400">{new Date(u.createdAt).toLocaleDateString()}</p>
                            </div>
                          </div>
                        )) : <p className="text-xs text-slate-500 italic">No recent data.</p>}
                      </div>
                    </>
                  )}

                  {/* Context: Retention */}
                  {selectedMetric.startsWith('retention') && (
                    <>
                      <div className="space-y-2">
                        <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">Metric Definition</p>
                        <p className="text-sm font-medium text-slate-300 leading-relaxed">
                          {selectedMetric === 'retention_rate' ? 'Active Users tracked via Login Activity.' : 'Users with no login activity for > 7 days.'}
                        </p>
                      </div>
                      <div className="space-y-4">
                        <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">At-Risk Segment</p>
                        {pillarMetrics.retention.atRiskUsers.length > 0 ? pillarMetrics.retention.atRiskUsers.map(u => (
                          <div key={u.id} className="flex items-center gap-3 p-3 rounded-xl bg-white/5 border border-white/10">
                            <div className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center text-xs font-bold text-amber-400">{u.displayName.charAt(0)}</div>
                            <div>
                              <p className="text-xs font-bold">{u.displayName}</p>
                              <p className="text-[10px] text-slate-400">Last seen: {new Date(u.lastLogin).toLocaleDateString()}</p>
                            </div>
                          </div>
                        )) : <p className="text-xs text-slate-500 italic">No at-risk users found.</p>}
                      </div>
                    </>
                  )}

                  {/* Context: Monetization */}
                  {selectedMetric.startsWith('monetization') && (
                    <>
                      <div className="space-y-2">
                        <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">Metric Definition</p>
                        <p className="text-sm font-medium text-slate-300 leading-relaxed">
                          Monthly Recurring Revenue (MRR) estimated based on {pillarMetrics.monetization.premiumCount} active Premium users.
                        </p>
                        <p className="text-[10px] font-mono text-amber-400/80 bg-amber-900/20 p-2 rounded-lg border border-amber-900/50 mt-2">
                          Calculation: Premium Users × $10.00/mo (Standard Plan)
                        </p>
                      </div>
                      <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-400">
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-[10px] font-bold uppercase tracking-widest">Premium Base</span>
                          <span className="text-xs font-black">{pillarMetrics.monetization.premiumCount} Users</span>
                        </div>
                        <div className="w-full h-1 bg-white/10 rounded-full overflow-hidden">
                          <div className="h-full bg-amber-500" style={{ width: `${pillarMetrics.monetization.conversionRate}%` }} />
                        </div>
                      </div>
                    </>
                  )}

                  {/* Context: Stability */}
                  {selectedMetric.startsWith('stability') && (
                    <>
                      <div className="space-y-2">
                        <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">Metric Definition</p>
                        <p className="text-sm font-medium text-slate-300 leading-relaxed">
                          {selectedMetric === 'stability_error'
                            ? 'Ratio of FAILED status logs against TOTAL attempted relays within the selected window.'
                            : 'Absolute count of relay events marked as FAILED in the telemetry logs.'}
                        </p>
                      </div>
                      <div className="space-y-4">
                        <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">Recent Errors</p>
                        {pillarMetrics.stability.recentErrors.length > 0 ? pillarMetrics.stability.recentErrors.map(l => (
                          <div key={l.id} className="p-3 rounded-xl bg-rose-500/10 border border-rose-500/20 space-y-1">
                            <p className="text-[10px] font-bold text-rose-400 uppercase">{l.triggerType}</p>
                            <p className="text-[10px] text-slate-400 truncate">{l.diagnostic || 'Unknown Error'}</p>
                          </div>
                        )) : (
                          <div className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs font-bold flex items-center gap-2">
                            <CheckCircle2 size={14} /> Systems Nominal
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* USERS REGISTRY */}
      {/* ... (Users Registry Tab - unchanged structure, just keeping it here) */}
      {activeTab === 'users' && (
        <div className="space-y-6 animate-in slide-in-from-bottom-4">
          {/* Search & Filter Header */}
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4 bg-white p-4 rounded-2xl border border-slate-200 shadow-sm">
            <div className="flex items-center gap-3 w-full sm:w-auto">
              <div className="p-2 bg-slate-100 rounded-lg text-slate-500">
                <Users size={20} />
              </div>
              <div>
                <h3 className="text-sm font-bold text-slate-900">User Registry</h3>
                <p className="text-xs text-slate-500">{users.length} Registered Operators</p>
              </div>
            </div>

            <div className="relative w-full sm:w-80">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={userSearch}
                onChange={e => setUserSearch(e.target.value)}
                placeholder="Search by name or email..."
                className="w-full bg-slate-50 border border-slate-200 rounded-xl py-2 pl-10 pr-4 text-sm font-medium outline-none focus:border-blue-500 transition-all"
              />
            </div>
          </div>

          {/* Users Table */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="py-4 px-6 text-xs font-bold text-slate-500 uppercase tracking-wider">Operator</th>
                    <th className="py-4 px-6 text-xs font-bold text-slate-500 uppercase tracking-wider">Status</th>
                    <th className="py-4 px-6 text-xs font-bold text-slate-500 uppercase tracking-wider">Tier</th>
                    <th
                      className="py-4 px-6 text-xs font-bold text-slate-500 uppercase tracking-wider cursor-pointer hover:text-blue-600 transition-colors"
                      onClick={() => requestSort('createdAt')}
                    >
                      <div className="flex items-center gap-1">
                        Join Date/Time {sortConfig?.key === 'createdAt' && (sortConfig.direction === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />)}
                      </div>
                    </th>
                    <th
                      className="py-4 px-6 text-xs font-bold text-slate-500 uppercase tracking-wider cursor-pointer hover:text-blue-600 transition-colors"
                      onClick={() => requestSort('lastLogin')}
                    >
                      <div className="flex items-center gap-1">
                        Last Login {sortConfig?.key === 'lastLogin' && (sortConfig.direction === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />)}
                      </div>
                    </th>
                    <th className="py-4 px-6 text-xs font-bold text-slate-500 uppercase tracking-wider text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {sortedUsers.map(u => (
                    <tr key={u.id} className="hover:bg-slate-50/80 transition-colors group">
                      <td className="py-4 px-6">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-600 font-bold text-sm">
                            {u.displayName.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <p className="text-sm font-bold text-slate-900">{u.displayName}</p>
                            <p className="text-xs text-slate-500 font-mono">{u.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex items-center gap-2">
                          <div className={`w-2 h-2 rounded-full ${u.isActive !== false ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                          <span className="text-xs font-medium text-slate-600">{u.isActive !== false ? 'Active' : 'Inactive'}</span>
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        {u.tier === UserTier.PREMIUM ? (
                          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-indigo-50 text-indigo-600 border border-indigo-100">
                            <Zap size={10} fill="currentColor" /> Premium
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-600 border border-slate-200">
                            Free
                          </span>
                        )}
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex flex-col">
                          <span className="text-sm font-bold text-slate-700">{new Date(u.createdAt).toLocaleDateString()}</span>
                          <span className="text-[10px] text-slate-400 font-mono">{new Date(u.createdAt).toLocaleTimeString()}</span>
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        {u.lastLogin ? (
                          <div className="flex flex-col">
                            <span className="text-sm font-bold text-slate-700">{new Date(u.lastLogin).toLocaleDateString()}</span>
                            <span className="text-[10px] text-slate-400 font-mono">{new Date(u.lastLogin).toLocaleTimeString()}</span>
                          </div>
                        ) : (
                          <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Never</span>
                        )}
                      </td>
                      <td className="py-4 px-6 text-right">
                        <div className="flex items-center justify-end gap-2 opacity-60 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={() => handleTogglePremium(u)}
                            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${u.tier === UserTier.PREMIUM ? 'bg-slate-100 text-slate-600 hover:bg-slate-200' : 'bg-blue-50 text-blue-600 hover:bg-blue-100'}`}
                          >
                            {u.tier === UserTier.PREMIUM ? 'Downgrade' : 'Promote'}
                          </button>
                          <button
                            onClick={() => handleDeleteUser(u)}
                            className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete User"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {users.length === 0 && (
              <div className="p-12 text-center text-slate-400 text-sm">No users found in registry.</div>
            )}
          </div>
        </div>
      )}

      {/* MESSAGING & ORCHESTRATION */}
      {/* ... (Messaging Tab - mostly unmodified except for handlers above) ... */}
      {activeTab === 'messaging' && (
        <div className="space-y-8 animate-in slide-in-from-bottom-4">
          {/* Messaging Sub-nav */}
          <div className="flex gap-2 p-1 bg-white rounded-xl w-fit border border-slate-200 overflow-x-auto shadow-sm">
            {[
              { id: 'logs', label: 'Telemetry Logs', icon: () => <FileText size={14} /> },
              { id: 'broadcasts', label: 'Broadcasts', icon: () => <Megaphone size={14} /> },
              { id: 'templates', label: 'Templates', icon: () => <Layout size={14} /> },
              { id: 'associations', label: 'Triggers', icon: () => <Link size={14} /> },
              { id: 'library', label: 'Context Library', icon: () => <Book size={14} /> }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setMessagingSubTab(tab.id as any)}
                className={`px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest flex items-center gap-2 transition-all whitespace-nowrap ${messagingSubTab === tab.id ? 'bg-slate-900 text-white shadow-lg' : 'text-slate-500 hover:text-slate-900'}`}
              >
                {tab.icon()} {tab.label}
              </button>
            ))}
          </div>

          {/* LOGS VIEW */}
          {messagingSubTab === 'logs' && (
            <div className="bg-white rounded-[2rem] border border-slate-200 overflow-hidden shadow-sm">
              <div className="p-6 border-b border-slate-200 bg-slate-50/50">
                <div className="flex items-center gap-4">
                  <Search size={16} className="text-slate-400" />
                  <input
                    value={relaySearch}
                    onChange={e => setRelaySearch(e.target.value)}
                    placeholder="Search logs by email, ID or status..."
                    className="bg-transparent text-xs font-bold w-full outline-none placeholder:text-slate-400 text-slate-900"
                  />
                </div>
              </div>
              <div className="max-h-[600px] overflow-y-auto custom-scrollbar">
                {emailLogs.filter(l => JSON.stringify(l).toLowerCase().includes(relaySearch.toLowerCase())).map(log => (
                  <div
                    key={log.id}
                    onClick={() => setSelectedLogId(log.id)}
                    className="p-5 border-b border-slate-100 hover:bg-blue-50/50 cursor-pointer transition-all flex items-center justify-between group"
                  >
                    <div className="flex items-center gap-4">
                      <div className={`p-2 rounded-lg ${log.status === 'SENT' ? 'bg-emerald-500/10 text-emerald-500' : log.status === 'FAILED' ? 'bg-rose-500/10 text-rose-500' : 'bg-amber-500/10 text-amber-500'}`}>
                        {log.status === 'SENT' ? <CheckCircle2 size={16} /> : log.status === 'FAILED' ? <AlertCircle size={16} /> : <Loader2 size={16} className="animate-spin" />}
                      </div>
                      <div>
                        <p className="text-xs font-bold text-slate-900">{log.subject || 'No Subject'}</p>
                        <div className="flex items-center gap-2 text-[10px] text-slate-500 font-mono mt-1">
                          <span>{log.recipient}</span>
                          <span className="w-1 h-1 bg-slate-300 rounded-full" />
                          <span>{new Date(log.timestamp).toLocaleString()}</span>
                        </div>
                      </div>
                    </div>
                    <ChevronRight size={16} className="text-slate-400 opacity-20 group-hover:opacity-100 transition-opacity" />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* BROADCASTS VIEW */}
          {messagingSubTab === 'broadcasts' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="bg-white p-8 rounded-[2rem] border border-slate-200 shadow-sm space-y-6">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <Megaphone className="text-blue-600" size={20} />
                    <h3 className="text-lg font-black uppercase tracking-tight text-slate-900">Deploy Pulse</h3>
                  </div>
                  {protocolStatus !== 'IDLE' && (
                    <div className={`flex items-center gap-2 px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest ${protocolStatus === 'SYNCING' ? 'bg-blue-50 text-blue-600' : protocolStatus === 'SYNCED' ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-600'}`}>
                      {protocolStatus === 'SYNCING' && <RefreshCcw size={10} className="animate-spin" />}
                      {protocolStatus}
                    </div>
                  )}
                </div>
                <form onSubmit={handleDeployPulse} className="space-y-4">
                  {/* ... (Form Inputs remain the same) ... */}
                  <input
                    value={pulseForm.title}
                    onChange={e => setPulseForm({ ...pulseForm, title: e.target.value })}
                    placeholder="Broadcast Title"
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-xs font-bold outline-none focus:border-blue-500"
                  />
                  <textarea
                    value={pulseForm.message}
                    onChange={e => setPulseForm({ ...pulseForm, message: e.target.value })}
                    placeholder="Message Content..."
                    className="w-full h-32 bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-xs font-medium outline-none focus:border-blue-500 resize-none"
                  />

                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest pl-1">Primary Sector</label>
                      <select
                        value={pulseForm.targetGroup}
                        onChange={e => setPulseForm({ ...pulseForm, targetGroup: e.target.value as any })}
                        className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-3 text-[10px] font-black uppercase outline-none"
                      >
                        <option value="ALL">All Units</option>
                        <option value="PREMIUM">Premium</option>
                        <option value="FREE">Free Tier</option>
                        <option value="ADMINS">Admins</option>
                      </select>
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest pl-1">Alert Level</label>
                      <select
                        value={pulseForm.priority}
                        onChange={e => setPulseForm({ ...pulseForm, priority: e.target.value as any })}
                        className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-3 text-[10px] font-black uppercase outline-none"
                      >
                        <option value="NORMAL">Normal</option>
                        <option value="CRITICAL">Critical</option>
                      </select>
                    </div>
                  </div>

                  {/* Audience Segmentation */}
                  <div className="space-y-2 pt-2 border-t border-slate-100">
                    <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest pl-1 flex items-center gap-2">
                      <Filter size={10} /> Audience Segmentation
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      <select
                        value={audienceFilter.type}
                        onChange={e => setAudienceFilter({ ...audienceFilter, type: e.target.value as any })}
                        className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-3 text-[10px] font-black uppercase outline-none"
                      >
                        <option value="ALL">Full Registry</option>
                        <option value="ACTIVE_RECENTLY">Active Recently</option>
                        <option value="NEW_USERS">New Joins</option>
                        <option value="NEVER_LOGGED_IN">Dormant (Never Active)</option>
                      </select>
                      {audienceFilter.type !== 'ALL' && audienceFilter.type !== 'NEVER_LOGGED_IN' && (
                        <div className="relative">
                          <input
                            type="number"
                            value={audienceFilter.days}
                            onChange={e => setAudienceFilter({ ...audienceFilter, days: parseInt(e.target.value) || 0 })}
                            className="w-full bg-slate-50 border border-slate-200 rounded-xl pl-3 pr-8 py-3 text-[10px] font-black outline-none"
                            placeholder="Days"
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[9px] font-bold text-slate-400">DAYS</span>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center justify-between px-3 py-2 bg-blue-50 rounded-xl border border-blue-100">
                      <span className="text-[9px] font-bold text-blue-600 uppercase tracking-wide">Estimated Reach</span>
                      <span className="text-xs font-black text-slate-900">{targetedUsers.length} Operators</span>
                    </div>
                  </div>

                  {/* Transmission Vector */}
                  <div className="space-y-2 pt-2 border-t border-slate-100">
                    <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest pl-1 flex items-center gap-2">
                      <Radio size={10} /> Transmission Vector
                    </label>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => toggleChannel('IN_APP')}
                        className={`flex-1 py-3 px-2 rounded-xl border flex flex-col items-center justify-center gap-1 transition-all ${deliveryChannels.includes('IN_APP') ? 'bg-slate-800 border-slate-900 text-white' : 'bg-white border-slate-200 text-slate-400 hover:border-slate-300'}`}
                      >
                        <Bell size={14} />
                        <span className="text-[8px] font-black uppercase tracking-widest">In-App Relay</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => toggleChannel('EMAIL')}
                        className={`flex-1 py-3 px-2 rounded-xl border flex flex-col items-center justify-center gap-1 transition-all ${deliveryChannels.includes('EMAIL') ? 'bg-indigo-600 border-indigo-700 text-white' : 'bg-white border-slate-200 text-slate-400 hover:border-slate-300'}`}
                      >
                        <MailCheck size={14} />
                        <span className="text-[8px] font-black uppercase tracking-widest">Email</span>
                      </button>
                    </div>
                  </div>

                  <button disabled={deliveryChannels.length === 0} className="w-full py-3 bg-blue-600 text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-blue-500 transition-all shadow-sm disabled:opacity-50">
                    Dispatch Broadcast
                  </button>
                </form>
              </div>

              <div className="lg:col-span-2 space-y-4">
                <div className="flex items-center justify-between mb-4 pl-1">
                  <h3 className="text-xs font-black uppercase tracking-widest text-slate-500">Active Broadcasts</h3>
                  <div className="relative w-48">
                    <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      value={broadcastSearch}
                      onChange={e => setBroadcastSearch(e.target.value)}
                      placeholder="Search..."
                      className="w-full bg-white border border-slate-200 rounded-lg py-1 pl-8 pr-3 text-[10px] font-bold outline-none focus:border-blue-500 transition-all"
                    />
                  </div>
                </div>
                {filteredBroadcasts.map(b => (
                  <div key={b.id} className="bg-white p-6 rounded-[2rem] border border-slate-200 shadow-sm flex justify-between items-start">
                    <div className="space-y-2 w-full">
                      <div className="flex items-center justify-between w-full">
                        <div className="flex items-center gap-3">
                          <span className={`px-2 py-0.5 rounded text-[9px] font-black uppercase ${b.priority === 'CRITICAL' ? 'bg-rose-500/10 text-rose-500' : 'bg-blue-500/10 text-blue-500'}`}>{b.priority}</span>
                          <h4 className="text-sm font-bold text-slate-900">{b.title}</h4>
                        </div>
                        <div className="flex gap-1">
                          {b.channels?.includes('IN_APP') && <Bell size={12} className="text-slate-400" />}
                          {b.channels?.includes('EMAIL') && <MailCheck size={12} className="text-indigo-400" />}
                        </div>
                      </div>
                      <p className="text-xs text-slate-500">{b.message}</p>
                      <div className="flex items-center gap-4 text-[9px] font-mono text-slate-400 opacity-80 pt-2 border-t border-slate-50 mt-3">
                        <span className="flex items-center gap-1"><Target size={10} /> {b.targetGroup}</span>
                        {b.audienceFilter && b.audienceFilter.type !== 'ALL' && (
                          <span className="flex items-center gap-1 text-indigo-500"><Filter size={10} /> {b.audienceFilter.type.replace('_', ' ')} {b.audienceFilter.days ? `(${b.audienceFilter.days}d)` : ''}</span>
                        )}
                        <span className="ml-auto">{new Date(b.timestamp).toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* TEMPLATES VIEW */}
          {messagingSubTab === 'templates' && (
            <div className="space-y-6">
              <div className="flex flex-col sm:flex-row justify-between items-center gap-4 bg-white p-4 rounded-2xl border border-slate-200 shadow-sm">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-slate-100 rounded-lg text-slate-500">
                    <Layout size={20} />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-slate-900">Message Templates</h3>
                    <p className="text-xs text-slate-500">{templates.length} Active Payloads</p>
                  </div>
                </div>
                <div className="relative w-full sm:w-64">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    value={templateSearch}
                    onChange={e => setTemplateSearch(e.target.value)}
                    placeholder="Search templates..."
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl py-2 pl-10 pr-4 text-sm font-medium outline-none focus:border-blue-500 transition-all"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredTemplates.map(t => (
                <div
                  key={t.id}
                  className="bg-white p-6 rounded-[2rem] border border-slate-200 shadow-sm hover:border-blue-500/30 transition-all group relative overflow-hidden"
                >
                  <div className="flex justify-between items-start mb-4">
                    <div className="p-3 bg-slate-50 rounded-xl text-slate-500 group-hover:text-blue-600 transition-colors">
                      <Layout size={20} />
                    </div>
                    <button
                      onClick={async (e) => {
                        e.stopPropagation();
                        setLoadingTemplateId(t.id);
                        try {
                          // Fetch freshest data from Firestore before opening edit modal
                          const freshTemplate = await firebaseService.getTemplate(t.id);
                          const targetTemplate = freshTemplate || t;

                          setSelectedTemplateId(t.id);
                          setTemplateEditForm({
                            ...targetTemplate,
                            subject: targetTemplate.subject || '',
                            body: targetTemplate.body || ''
                          });
                        } catch (err) {
                          console.error("Failed to fetch fresh template", err);
                          // Fallback to state value if fetch fails
                          setSelectedTemplateId(t.id);
                          setTemplateEditForm({
                            ...t,
                            subject: t.subject || '',
                            body: t.body || ''
                          });
                        } finally {
                          setLoadingTemplateId(null);
                        }
                      }}
                      disabled={loadingTemplateId === t.id}
                      className="px-4 py-2 bg-blue-600 text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-blue-700 transition-all shadow-md flex items-center gap-2 opacity-100"
                    >
                      {loadingTemplateId === t.id ? (
                        <Activity className="animate-spin" size={12} />
                      ) : null}
                      Edit
                    </button>
                  </div>
                  <h4 className="text-sm font-bold text-slate-900 mb-1">{t.name}</h4>
                  <p className="text-[10px] text-slate-500 font-mono truncate">{t.subject}</p>

                  <div className="mt-4 pt-4 border-t border-slate-50 flex items-center gap-2 text-[8px] font-bold text-slate-400 uppercase tracking-tight">
                    <Clock size={10} />
                    <span>Last updated: {t.lastModified ? new Date(t.lastModified).toLocaleString() : 'System Default'}</span>
                  </div>
                </div>
                ))}`

              </div>
            </div>
          )}

          {/* ASSOCIATIONS VIEW */}
          {messagingSubTab === 'associations' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="bg-white p-8 rounded-[2rem] border border-slate-200 shadow-sm space-y-6">
                <div className="flex items-center gap-3 mb-2">
                  <Link className="text-purple-600" size={20} />
                  <h3 className="text-lg font-black uppercase tracking-tight text-slate-900">New Trigger</h3>
                </div>
                <div className="space-y-4">
                  <select
                    value={newAssoc.systemEvent}
                    onChange={e => setNewAssoc({ ...newAssoc, systemEvent: e.target.value as any })}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-xs font-bold outline-none"
                  >
                    <option value="">Select System Event...</option>
                    <option value="welcome_new_user">Welcome New User</option>
                    <option value="created">Task Created</option>
                    <option value="completed">Task Completed</option>
                    <option value="subtask">Subtask Feedback</option>
                    <option value="daily">Daily Pulse</option>
                    <option value="nudge_urgent">Urgent Stagnation Nudge</option>
                    <option value="nudge_strategic">Strategic Potential Nudge</option>
                    <option value="inactivity_7_days">Inactivity (7 Days)</option>
                    <option value="streak_7_day">Streak (7 Days)</option>
                    <option value="partner_request">Partner Request</option>
                    <option value="broadcast">Admin Broadcast</option>
                  </select>
                  <select
                    value={newAssoc.emailTask}
                    onChange={e => setNewAssoc({ ...newAssoc, emailTask: e.target.value })}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-xs font-bold outline-none"
                  >
                    <option value="">Select Template...</option>
                    {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                  <div className="grid grid-cols-2 gap-3">
                    <select
                      value={newAssoc.schedule}
                      onChange={e => setNewAssoc({ ...newAssoc, schedule: e.target.value as any })}
                      className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-xs font-bold outline-none"
                    >
                      <option value="realtime">Realtime</option>
                      <option value="daily">Daily</option>
                      <option value="weekly">Weekly</option>
                    </select>
                    <select
                      value={newAssoc.deliveryMode}
                      onChange={e => setNewAssoc({ ...newAssoc, deliveryMode: e.target.value as any })}
                      className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-xs font-bold outline-none"
                    >
                      <option value="per_user">Per User</option>
                      <option value="broadcast">Broadcast</option>
                    </select>
                  </div>
                  <button
                    onClick={handleCreateAssoc}
                    disabled={false}
                    className="w-full py-3 bg-purple-600 text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-purple-500 transition-all shadow-sm"
                  >
                    Bind Association
                  </button>
                </div>
              </div>

              <div className="lg:col-span-2 space-y-4">
                <div className="flex items-center justify-between mb-2 pl-1">
                  <h3 className="text-xs font-black uppercase tracking-widest text-slate-500">Active Triggers</h3>
                  <div className="relative w-48">
                    <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      value={triggerSearch}
                      onChange={e => setTriggerSearch(e.target.value)}
                      placeholder="Search..."
                      className="w-full bg-white border border-slate-200 rounded-lg py-1 pl-8 pr-3 text-[10px] font-bold outline-none focus:border-blue-500 transition-all"
                    />
                  </div>
                </div>
                {filteredAssociations.map(assoc => (
                  <div key={assoc.id} className="bg-white p-5 rounded-[2rem] border border-slate-200 shadow-sm flex justify-between items-center group">
                    <div className="flex items-center gap-6">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-purple-500/10 flex items-center justify-center text-purple-600">
                          <Zap size={14} />
                        </div>
                        <span className="text-xs font-bold uppercase text-slate-700">{assoc.systemEvent}</span>
                      </div>
                      <ArrowRight size={14} className="text-slate-300" />
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-blue-500/10 flex items-center justify-center text-blue-600">
                          <Layout size={14} />
                        </div>
                        <span className="text-xs font-bold uppercase text-slate-700">{assoc.emailTask}</span>
                      </div>
                      <div className="flex items-center gap-2 ml-2">
                        <span className={`px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider ${
                          (assoc.schedule || 'realtime') === 'daily' ? 'bg-amber-100 text-amber-700' :
                          (assoc.schedule || 'realtime') === 'weekly' ? 'bg-indigo-100 text-indigo-700' :
                          'bg-emerald-100 text-emerald-700'
                        }`}>
                          {assoc.schedule || 'realtime'}
                        </span>
                        <span className={`px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider ${
                          (assoc.deliveryMode || 'per_user') === 'broadcast' ? 'bg-purple-100 text-purple-700' : 'bg-slate-100 text-slate-600'
                        }`}>
                          {(assoc.deliveryMode || 'per_user').replace('_', ' ')}
                        </span>
                      </div>
                    </div>
                    <button onClick={() => handleDeleteAssoc(assoc.id)} className="p-2 text-slate-400 hover:text-rose-500 opacity-0 group-hover:opacity-100 transition-all">
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ... (Context Library, Social Links, Trust CMS, Recovery - Unchanged in structure but recovery actions replaced above) ... */}
          {/* CONTEXT LIBRARY VIEW - Skipped changes */}
          {messagingSubTab === 'library' && (
            <div className="bg-white p-10 rounded-[3rem] border border-slate-200 shadow-sm space-y-8">
              {/* ... (Context Library Content - same as original) */}
              <div className="flex items-center gap-4">
                <div className="p-3 bg-indigo-500/10 text-indigo-500 rounded-2xl"><BookOpen size={24} /></div>
                <div>
                  <h3 className="text-xl font-black uppercase tracking-tight text-slate-900">Context Library</h3>
                  <p className="text-[10px] font-mono text-slate-500 uppercase tracking-[0.3em]">Available Payload Variables</p>
                </div>
              </div>
              {/* ... (Grid Content) ... */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="space-y-4">
                  <h4 className="text-[10px] font-black text-slate-900 uppercase tracking-widest pl-2 border-l-2 border-indigo-500">Universal Variables</h4>
                  <div className="space-y-2">
                    {TEMPLATE_VARIABLES.map(v => (
                      <div key={v.key} className="p-4 bg-slate-50 border border-slate-200 rounded-xl group hover:border-indigo-500/30 transition-all">
                        <div className="flex justify-between items-start mb-2">
                          <code className="text-xs font-bold text-indigo-600 bg-indigo-500/10 px-2 py-0.5 rounded">{`{{${v.key}}}`}</code>
                          <span className="text-[9px] font-black uppercase text-slate-400">{v.type}</span>
                        </div>
                        <p className="text-[10px] text-slate-500 leading-relaxed">{v.description}</p>
                        <p className="text-[9px] font-mono text-slate-400 opacity-50 mt-1">Ex: {v.example}</p>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="space-y-4">
                  <h4 className="text-[10px] font-black text-slate-900 uppercase tracking-widest pl-2 border-l-2 border-purple-500">Event Requirements</h4>
                  <div className="space-y-2">
                    {Object.entries(EVENT_REQUIRED_CONTEXT).map(([event, vars]) => (
                      <div key={event} className="p-4 bg-slate-50 border border-slate-200 rounded-xl">
                        <div className="flex items-center gap-2 mb-2">
                          <Zap size={12} className="text-purple-500" />
                          <span className="text-[10px] font-black uppercase tracking-widest text-slate-700">{event}</span>
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {vars.map(v => (
                            <span key={v} className="text-[9px] font-mono bg-white border border-slate-100 px-1.5 py-0.5 rounded text-slate-500">{v}</span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* SOCIAL LINKS TAB */}
      {activeTab === 'social_links' && <SocialLinksTab />}

      {/* TRUST CMS - Unchanged */}
      {activeTab === 'trust_cms' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 animate-in slide-in-from-bottom-4">
          {trustDocuments.map(doc => (
            <div key={doc.id} className="bg-white p-8 rounded-[2.5rem] border border-slate-200 shadow-sm">
              <div className="flex justify-between items-start mb-6">
                <div className="flex items-center gap-4">
                  <div className="p-4 bg-blue-600/10 text-blue-600 rounded-2xl">
                    <Shield size={24} />
                  </div>
                  <div>
                    <h3 className="text-lg font-black uppercase tracking-tight text-slate-900">{doc.title}</h3>
                    <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">{doc.category}</span>
                  </div>
                </div>
                <span className={`px-3 py-1 rounded-lg text-[9px] font-black uppercase tracking-widest ${doc.status === 'PUBLISHED' ? 'bg-emerald-500/10 text-emerald-500' : 'bg-amber-500/10 text-amber-500'}`}>
                  {doc.status}
                </span>
              </div>
              <div className="flex gap-4 border-t border-slate-200 pt-6">
                <button className="flex-1 py-3 bg-slate-50 border border-slate-200 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-slate-100 transition-all">Edit Protocol</button>
                <button className="px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-400 hover:text-rose-500 transition-all">
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
          <div className="bg-white p-8 rounded-[2.5rem] border-2 border-dashed border-slate-200 flex flex-col items-center justify-center gap-4 opacity-50 hover:opacity-100 transition-all cursor-pointer min-h-[240px]">
            <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center">
              <Plus size={32} className="text-slate-400" />
            </div>
            <p className="text-xs font-black uppercase tracking-widest text-slate-400">Initialize New Protocol</p>
          </div>
        </div>
      )}

      {/* LEDGER */}
      {activeTab === 'ledger' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 animate-in slide-in-from-bottom-4">
          {/* Stripe Config */}
          <div className="bg-white p-8 rounded-[2.5rem] border border-slate-200 shadow-sm space-y-6">
            <div className="flex items-center gap-3 mb-2">
              <CreditCard className="text-indigo-600" size={24} />
              <h3 className="text-xl font-black uppercase tracking-tight text-slate-900">Ledger Config (Stripe)</h3>
            </div>
            <form onSubmit={handleSaveStripeConfig} className="space-y-4">
              <div className="space-y-2">
                <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">Publishable Key</label>
                <input
                  type={showSecretKeys ? "text" : "password"}
                  value={stripeForm.stripePublicKey}
                  onChange={e => setStripeForm({ ...stripeForm, stripePublicKey: e.target.value })}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-xs font-mono font-bold outline-none focus:border-indigo-500"
                />
              </div>
              <div className="space-y-2">
                <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">Secret Key</label>
                <input
                  type={showSecretKeys ? "text" : "password"}
                  value={stripeForm.stripeSecretKey}
                  onChange={e => setStripeForm({ ...stripeForm, stripeSecretKey: e.target.value })}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-xs font-mono font-bold outline-none focus:border-indigo-500"
                />
              </div>
              <div className="space-y-2">
                <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">Webhook Secret</label>
                <input
                  type={showSecretKeys ? "text" : "password"}
                  value={stripeForm.stripeWebhookSecret}
                  onChange={e => setStripeForm({ ...stripeForm, stripeWebhookSecret: e.target.value })}
                  placeholder="whsec_..."
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-xs font-mono font-bold outline-none focus:border-indigo-500"
                />
              </div>
              {/* Pricing Controls */}
              <div className="pt-2 border-t border-slate-100">
                <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1 mb-3 block">Premium Pricing</label>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <label className="text-[9px] font-bold text-slate-400 ml-1">Monthly ($)</label>
                    <div className="relative">
                      <DollarSign size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={((stripeForm.monthlyPriceCents || 600) / 100).toFixed(2)}
                        onChange={e => setStripeForm({ ...stripeForm, monthlyPriceCents: Math.round(parseFloat(e.target.value || '0') * 100) })}
                        className="w-full bg-slate-50 border border-slate-200 rounded-xl pl-8 pr-4 py-3 text-xs font-mono font-bold outline-none focus:border-indigo-500"
                      />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[9px] font-bold text-slate-400 ml-1">Yearly ($)</label>
                    <div className="relative">
                      <DollarSign size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={((stripeForm.yearlyPriceCents || 5900) / 100).toFixed(2)}
                        onChange={e => setStripeForm({ ...stripeForm, yearlyPriceCents: Math.round(parseFloat(e.target.value || '0') * 100) })}
                        className="w-full bg-slate-50 border border-slate-200 rounded-xl pl-8 pr-4 py-3 text-xs font-mono font-bold outline-none focus:border-indigo-500"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Connection Test Result */}
              {stripeConnection.status === 'success' && (
                <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-xl flex items-center gap-3">
                  <div className="w-2.5 h-2.5 bg-emerald-500 rounded-full animate-pulse" />
                  <div>
                    <span className="text-[10px] font-black text-emerald-700 uppercase tracking-widest">Connected</span>
                    <span className="text-[10px] text-emerald-600 ml-2">
                      {stripeConnection.accountName} &middot; <span className={`font-bold ${stripeConnection.mode === 'live' ? 'text-emerald-700' : 'text-amber-600'}`}>{stripeConnection.mode?.toUpperCase()} mode</span>
                    </span>
                  </div>
                </div>
              )}
              {stripeConnection.status === 'error' && (
                <div className="p-3 bg-rose-50 border border-rose-200 rounded-xl flex items-center gap-3">
                  <div className="w-2.5 h-2.5 bg-rose-500 rounded-full" />
                  <span className="text-[10px] font-black text-rose-700 uppercase tracking-widest">Connection Failed</span>
                  <span className="text-[10px] text-rose-500 ml-1">{stripeConnection.error}</span>
                </div>
              )}

              <div className="flex items-center justify-between pt-2">
                <div className="flex items-center gap-3">
                  <button type="button" onClick={() => setShowSecretKeys(!showSecretKeys)} className="text-[10px] font-bold text-slate-400 uppercase flex items-center gap-2">
                    {showSecretKeys ? <EyeOff size={14} /> : <Eye size={14} />} {showSecretKeys ? 'Obscure Keys' : 'Reveal Keys'}
                  </button>
                  <button type="button" disabled={stripeConnection.status === 'testing'} onClick={async () => {
                    setStripeConnection({ status: 'testing' });
                    try {
                      const h = await getAuthHeaders();
                      const res = await fetch(`${API_BASE}/api/stripe/test-connection`, { method: 'POST', headers: h });
                      const data = await res.json();
                      if (data.connected) {
                        setStripeConnection({ status: 'success', mode: data.mode, accountName: data.accountName });
                      } else {
                        setStripeConnection({ status: 'error', error: data.error });
                      }
                    } catch (e: any) {
                      setStripeConnection({ status: 'error', error: e.message });
                    }
                  }} className="text-[10px] font-bold text-indigo-500 uppercase flex items-center gap-2 hover:text-indigo-700 transition-all disabled:opacity-50">
                    {stripeConnection.status === 'testing' ? <Loader2 className="animate-spin" size={14} /> : <Zap size={14} />} Test Connection
                  </button>
                </div>
                <button className="px-6 py-3 bg-indigo-600 text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-indigo-500 transition-all shadow-sm">
                  Sync Ledger
                </button>
              </div>
            </form>
          </div>

          {/* Promo Code Creator */}
          <div className="bg-white p-8 rounded-[2.5rem] border border-slate-200 shadow-sm space-y-6">
            <div className="flex items-center gap-3 mb-2">
              <Coins className="text-emerald-600" size={24} />
              <h3 className="text-xl font-black uppercase tracking-tight text-slate-900">Promotion Codes</h3>
            </div>
            <form onSubmit={handleCreatePromoCode} className="space-y-4">
              <div className="space-y-2">
                <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">Code</label>
                <input
                  value={promoForm.code}
                  onChange={e => setPromoForm({ ...promoForm, code: e.target.value.toUpperCase() })}
                  placeholder="e.g. LAUNCH50"
                  required
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-xs font-mono font-black uppercase outline-none focus:border-emerald-500 tracking-widest"
                />
              </div>
              <div className="space-y-2">
                <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">Discount Type</label>
                <div className="grid grid-cols-2 gap-2">
                  <button type="button" onClick={() => setPromoForm({ ...promoForm, discountType: 'percent' })}
                    className={`py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${promoForm.discountType === 'percent' ? 'bg-emerald-600 text-white shadow-sm' : 'bg-slate-50 text-slate-400 border border-slate-200'}`}>
                    Percent Off
                  </button>
                  <button type="button" onClick={() => setPromoForm({ ...promoForm, discountType: 'fixed' })}
                    className={`py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${promoForm.discountType === 'fixed' ? 'bg-emerald-600 text-white shadow-sm' : 'bg-slate-50 text-slate-400 border border-slate-200'}`}>
                    Fixed Amount
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">{promoForm.discountType === 'percent' ? 'Discount (%)' : 'Discount ($)'}</label>
                <div className="relative">
                  {promoForm.discountType === 'fixed' && <DollarSign size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />}
                  <input
                    type="number"
                    step={promoForm.discountType === 'percent' ? '1' : '0.01'}
                    min="0"
                    max={promoForm.discountType === 'percent' ? '100' : undefined}
                    value={promoForm.discountValue}
                    onChange={e => setPromoForm({ ...promoForm, discountValue: e.target.value })}
                    placeholder={promoForm.discountType === 'percent' ? 'e.g. 25' : 'e.g. 5.00'}
                    required
                    className={`w-full bg-slate-50 border border-slate-200 rounded-xl ${promoForm.discountType === 'fixed' ? 'pl-8' : 'pl-4'} pr-4 py-3 text-xs font-mono font-bold outline-none focus:border-emerald-500`}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <label className="text-[9px] font-bold text-slate-400 ml-1">Max Uses <span className="text-slate-300">(optional)</span></label>
                  <input
                    type="number" min="1"
                    value={promoForm.maxRedemptions}
                    onChange={e => setPromoForm({ ...promoForm, maxRedemptions: e.target.value })}
                    placeholder="Unlimited"
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-xs font-mono font-bold outline-none focus:border-emerald-500"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-[9px] font-bold text-slate-400 ml-1">Expires <span className="text-slate-300">(optional)</span></label>
                  <input
                    type="date"
                    value={promoForm.expiresAt}
                    onChange={e => setPromoForm({ ...promoForm, expiresAt: e.target.value })}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-xs font-mono font-bold outline-none focus:border-emerald-500"
                  />
                </div>
              </div>
              <div className="flex justify-end pt-2">
                <button type="submit" className="px-6 py-3 bg-emerald-600 text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-emerald-500 transition-all shadow-sm flex items-center gap-2">
                  <Plus size={14} /> Create Code
                </button>
              </div>
            </form>
          </div>

          {/* Active Promo Codes List */}
          <div className="bg-white p-8 rounded-[2.5rem] border border-slate-200 shadow-sm space-y-6 lg:col-span-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Hash className="text-indigo-500" size={24} />
                <h3 className="text-xl font-black uppercase tracking-tight text-slate-900">Promotion Codes</h3>
              </div>
              <button onClick={loadPromoCodes} disabled={promoLoading} className="px-4 py-2 bg-slate-100 rounded-lg text-[9px] font-black uppercase tracking-widest text-slate-500 hover:bg-slate-200 transition-all disabled:opacity-50 flex items-center gap-2">
                {promoLoading ? <Loader2 className="animate-spin" size={12} /> : <RefreshCw size={12} />} Refresh
              </button>
            </div>
            {promoLoading && promoCodes.length === 0 ? (
              <div className="py-12 flex items-center justify-center"><Loader2 className="animate-spin text-slate-300" size={24} /></div>
            ) : promoCodes.length === 0 ? (
              <div className="py-12 text-center text-[10px] text-slate-400 uppercase tracking-widest font-bold">No promotion codes found</div>
            ) : (
              <div className="space-y-3">
                {promoCodes.map(pc => (
                  <div key={pc.id} className={`flex items-center justify-between p-4 rounded-2xl border ${pc.active ? 'bg-slate-50 border-slate-200' : 'bg-slate-100/50 border-slate-200/60 opacity-70'}`}>
                    <div className="flex flex-col gap-1.5">
                      <div className="flex items-center gap-3">
                        <span className={`font-mono font-black text-sm tracking-widest ${pc.active ? 'text-slate-900' : 'text-slate-500 line-through'}`}>{pc.code}</span>
                        <span className="px-2.5 py-1 bg-emerald-100 text-emerald-700 rounded-lg text-[10px] font-bold">
                          {pc.coupon.percentOff ? `${pc.coupon.percentOff}% off` : `$${(pc.coupon.amountOff / 100).toFixed(2)} off`}
                        </span>
                        <span className={`px-2 py-0.5 rounded-md text-[9px] font-black uppercase tracking-widest ${pc.active ? 'bg-emerald-500/10 text-emerald-600' : 'bg-slate-300/30 text-slate-500'}`}>
                          {pc.active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 text-[10px] text-slate-400 font-bold flex-wrap">
                        <span>{pc.timesRedeemed} redeemed{pc.maxRedemptions ? ` / ${pc.maxRedemptions} max` : ''}</span>
                        {pc.created && <span>Created {new Date(pc.created).toLocaleDateString()}</span>}
                        {pc.expiresAt && <span>{pc.expiresAt < Date.now() ? 'Expired' : 'Expires'} {new Date(pc.expiresAt).toLocaleDateString()}</span>}
                      </div>
                      {pc.redemptions?.length > 0 && (
                        <div className="flex flex-col gap-1 mt-1">
                          {pc.redemptions.map((r: any, i: number) => (
                            <div key={i} className="flex items-center gap-3 text-[10px] text-slate-400 font-bold pl-1">
                              <CheckCircle2 size={10} className="text-emerald-500" />
                              <span>Redeemed {new Date(r.redeemedAt).toLocaleDateString()} at {new Date(r.redeemedAt).toLocaleTimeString()}</span>
                              {r.customerEmail && <span className="text-slate-500">by {r.customerEmail}</span>}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    {pc.active && (
                      <button onClick={() => handleDeactivatePromoCode(pc.id, pc.code)} className="p-2.5 text-slate-400 hover:text-rose-500 hover:bg-rose-50 rounded-xl transition-all" title="Deactivate">
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* FEEDBACK MANAGEMENT */}
      {activeTab === 'feedback' && (
        <div className="space-y-8 animate-in slide-in-from-bottom-4">
          {/* Filters */}
          <div className="flex flex-wrap gap-3 items-center">
            <div className="flex gap-2">
              {(['ALL', 'BUG', 'PERFORMANCE', 'FEATURE'] as const).map(t => (
                <button key={t} onClick={() => setFeedbackTypeFilter(t)}
                  className={`px-4 py-2 rounded-xl text-[9px] font-black uppercase tracking-widest transition-all ${
                    feedbackTypeFilter === t ? 'bg-blue-600 text-white shadow-sm' : 'bg-white text-slate-400 border border-slate-200 hover:bg-slate-50'
                  }`}>
                  {t === 'ALL' ? 'All Types' : t === 'BUG' ? 'Bugs' : t === 'PERFORMANCE' ? 'Performance' : 'Features'}
                </button>
              ))}
            </div>
            <div className="w-px h-6 bg-slate-200" />
            <div className="flex gap-2">
              {(['ALL', 'NEW', 'IN_PROGRESS', 'COMPLETED', 'WONT_FIX'] as const).map(s => (
                <button key={s} onClick={() => setFeedbackStatusFilter(s)}
                  className={`px-4 py-2 rounded-xl text-[9px] font-black uppercase tracking-widest transition-all ${
                    feedbackStatusFilter === s ? 'bg-slate-900 text-white shadow-sm' : 'bg-white text-slate-400 border border-slate-200 hover:bg-slate-50'
                  }`}>
                  {s === 'ALL' ? 'All Status' : s === 'NEW' ? 'New' : s === 'IN_PROGRESS' ? 'In Progress' : s === 'COMPLETED' ? 'Completed' : "Won't Fix"}
                </button>
              ))}
            </div>
            <div className="ml-auto">
              <button onClick={loadFeedback} disabled={feedbackLoading} className="px-4 py-2 bg-slate-100 rounded-lg text-[9px] font-black uppercase tracking-widest text-slate-500 hover:bg-slate-200 transition-all disabled:opacity-50 flex items-center gap-2">
                {feedbackLoading ? <Loader2 className="animate-spin" size={12} /> : <RefreshCw size={12} />} Refresh
              </button>
            </div>
          </div>

          {/* Feedback List */}
          {feedbackLoading && feedbackItems.length === 0 ? (
            <div className="py-20 flex items-center justify-center"><Loader2 className="animate-spin text-slate-300" size={32} /></div>
          ) : (() => {
            const filtered = feedbackItems.filter(f =>
              (feedbackTypeFilter === 'ALL' || f.type === feedbackTypeFilter) &&
              (feedbackStatusFilter === 'ALL' || f.status === feedbackStatusFilter)
            );
            if (filtered.length === 0) return (
              <div className="py-20 text-center text-[10px] text-slate-400 uppercase tracking-widest font-bold">No feedback entries found</div>
            );
            return (
              <div className="space-y-4">
                {filtered.map(f => (
                  <div key={f.id} className={`bg-white p-6 rounded-[2rem] border shadow-sm ${
                    f.status === 'COMPLETED' ? 'border-emerald-200/60 opacity-70' :
                    f.status === 'WONT_FIX' ? 'border-slate-200/60 opacity-60' :
                    f.status === 'IN_PROGRESS' ? 'border-amber-200' : 'border-slate-200'
                  }`}>
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 space-y-3">
                        <div className="flex items-center gap-3 flex-wrap">
                          <span className={`px-2.5 py-1 rounded-lg text-[9px] font-black uppercase tracking-widest ${
                            f.type === 'BUG' ? 'bg-rose-100 text-rose-600' :
                            f.type === 'PERFORMANCE' ? 'bg-amber-100 text-amber-600' :
                            'bg-indigo-100 text-indigo-600'
                          }`}>
                            {f.type === 'BUG' ? 'Bug / Issue' : f.type === 'PERFORMANCE' ? 'Performance' : 'Feature Idea'}
                          </span>
                          <span className={`px-2.5 py-1 rounded-lg text-[9px] font-black uppercase tracking-widest ${
                            f.status === 'NEW' ? 'bg-blue-100 text-blue-600' :
                            f.status === 'IN_PROGRESS' ? 'bg-amber-100 text-amber-600' :
                            f.status === 'COMPLETED' ? 'bg-emerald-100 text-emerald-600' :
                            'bg-slate-200 text-slate-500'
                          }`}>
                            {f.status === 'NEW' ? 'New' : f.status === 'IN_PROGRESS' ? 'In Progress' : f.status === 'COMPLETED' ? 'Completed' : "Won't Fix"}
                          </span>
                          <span className="text-[10px] text-slate-400 font-bold">{f.userName} ({f.userEmail})</span>
                          <span className="text-[10px] text-slate-300">{new Date(f.createdAt).toLocaleDateString()} {new Date(f.createdAt).toLocaleTimeString()}</span>
                        </div>
                        <p className={`text-sm leading-relaxed ${f.status === 'WONT_FIX' ? 'text-slate-400 line-through' : 'text-slate-700'}`}>{f.message}</p>
                        {f.adminNotes && (
                          <div className="flex items-start gap-2 p-3 bg-slate-50 rounded-xl">
                            <Info size={12} className="text-slate-400 mt-0.5 shrink-0" />
                            <p className="text-[11px] text-slate-500">{f.adminNotes}</p>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {f.status === 'NEW' && (
                          <button onClick={() => handleUpdateFeedbackStatus(f, 'IN_PROGRESS')}
                            className="px-3 py-2 bg-amber-50 text-amber-600 rounded-xl text-[9px] font-black uppercase tracking-widest hover:bg-amber-100 transition-all">
                            In Progress
                          </button>
                        )}
                        {(f.status === 'NEW' || f.status === 'IN_PROGRESS') && (
                          <button onClick={() => handleUpdateFeedbackStatus(f, 'COMPLETED')}
                            className="px-3 py-2 bg-emerald-50 text-emerald-600 rounded-xl text-[9px] font-black uppercase tracking-widest hover:bg-emerald-100 transition-all">
                            Complete
                          </button>
                        )}
                        {(f.status === 'NEW' || f.status === 'IN_PROGRESS') && (
                          <button onClick={() => handleUpdateFeedbackStatus(f, 'WONT_FIX')}
                            className="px-3 py-2 bg-slate-50 text-slate-400 rounded-xl text-[9px] font-black uppercase tracking-widest hover:bg-slate-100 transition-all">
                            Won't Fix
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            );
          })()}

          {/* Summary Stats */}
          {feedbackItems.length > 0 && (
            <div className="grid grid-cols-5 gap-4">
              {[
                { label: 'Total', count: feedbackItems.length, color: 'bg-slate-100 text-slate-600' },
                { label: 'New', count: feedbackItems.filter(f => f.status === 'NEW').length, color: 'bg-blue-50 text-blue-600' },
                { label: 'In Progress', count: feedbackItems.filter(f => f.status === 'IN_PROGRESS').length, color: 'bg-amber-50 text-amber-600' },
                { label: 'Completed', count: feedbackItems.filter(f => f.status === 'COMPLETED').length, color: 'bg-emerald-50 text-emerald-600' },
                { label: "Won't Fix", count: feedbackItems.filter(f => f.status === 'WONT_FIX').length, color: 'bg-slate-50 text-slate-500' },
              ].map(s => (
                <div key={s.label} className={`${s.color} p-4 rounded-2xl text-center`}>
                  <div className="text-2xl font-black">{s.count}</div>
                  <div className="text-[9px] font-black uppercase tracking-widest mt-1">{s.label}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* SYSTEM RECOVERY & CONFIG */}
      {activeTab === 'recovery' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 animate-in slide-in-from-bottom-4">
          {/* Postmark Config */}
          <div className="bg-white p-8 rounded-[2.5rem] border border-slate-200 shadow-sm space-y-6">
            <div className="flex items-center gap-3 mb-2">
              <Mail className="text-amber-500" size={24} />
              <h3 className="text-xl font-black uppercase tracking-tight text-slate-900">Relay Config (Postmark)</h3>
            </div>
            <form onSubmit={handleSavePostmarkConfig} className="space-y-4">
              <div className="space-y-2">
                <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">Server Token</label>
                <input
                  type={showSecretKeys ? "text" : "password"}
                  value={postmarkForm.serverToken}
                  onChange={e => setPostmarkForm({ ...postmarkForm, serverToken: e.target.value })}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-xs font-mono font-bold outline-none focus:border-amber-500"
                />
              </div>
              <div className="space-y-2">
                <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">From Address</label>
                <input
                  value={postmarkForm.fromEmail}
                  onChange={e => setPostmarkForm({ ...postmarkForm, fromEmail: e.target.value })}
                  placeholder="command@priori-ai.com"
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-xs font-bold outline-none focus:border-amber-500"
                />
              </div>
              <div className="flex justify-end pt-2">
                <button className="px-6 py-3 bg-amber-500 text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-amber-600 transition-all shadow-sm">
                  Update Protocol
                </button>
              </div>
            </form>
          </div>

          {/* Relay Test Tool */}
          <div className="bg-white p-8 rounded-[2.5rem] border border-slate-200 shadow-sm space-y-6 lg:col-span-2">
            <div className="flex items-center gap-3 mb-2">
              <Radio size={24} className="text-rose-500" />
              <h3 className="text-xl font-black uppercase tracking-tight text-slate-900">System Diagnostics</h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Infrastructure Audit */}
              <div className="p-6 bg-slate-50 rounded-2xl border border-slate-200">
                <div className="flex justify-between items-center mb-4">
                  <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">Infrastructure</span>
                  <button
                    onClick={handleRunDiagnostic}
                    disabled={isRunningDiag}
                    className="px-4 py-2 bg-slate-600 text-white rounded-lg text-[9px] font-black uppercase tracking-widest hover:bg-slate-700 transition-all disabled:opacity-50 flex items-center gap-2"
                  >
                    {isRunningDiag ? <Loader2 className="animate-spin" size={14} /> : <Activity size={14} />} Audit
                  </button>
                </div>
                {diagnosticResult ? (
                  <div className="space-y-2 font-mono text-[10px]">
                    <div className="flex justify-between border-b border-slate-200 pb-1">
                      <span className="text-slate-500">Library:</span>
                      <span className={diagnosticResult.library_installed ? "text-emerald-500 font-bold" : "text-rose-500 font-bold"}>{diagnosticResult.library_installed ? "INSTALLED" : "MISSING"}</span>
                    </div>
                    <div className="flex justify-between border-b border-slate-200 pb-1">
                      <span className="text-slate-500">Token:</span>
                      <span className={diagnosticResult.env_var_present ? "text-emerald-500 font-bold" : "text-rose-500 font-bold"}>{diagnosticResult.env_var_present ? "PRESENT" : "MISSING"}</span>
                    </div>
                    <div className="flex justify-between border-b border-slate-200 pb-1">
                      <span className="text-slate-500">Network:</span>
                      <span className={diagnosticResult.outbound_network ? "text-emerald-500 font-bold" : "text-rose-500 font-bold"}>{diagnosticResult.outbound_network ? "ONLINE" : "OFFLINE"}</span>
                    </div>
                    <div className="pt-1 text-[9px] text-slate-400 opacity-50">Rev: {diagnosticResult.container_revision?.substring(0, 8) || 'N/A'}</div>
                  </div>
                ) : (
                  <div className="h-24 flex items-center justify-center text-[10px] text-slate-400 opacity-50 uppercase tracking-widest">Awaiting Audit</div>
                )}
              </div>

              {/* Transmission Test */}
              <div className="p-6 bg-slate-50 rounded-2xl border border-slate-200">
                <div className="flex justify-between items-center mb-4">
                  <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">Transmission</span>
                  <button
                    onClick={runRelayTest}
                    disabled={relayTestStatus === 'TESTING'}
                    className="px-4 py-2 bg-rose-500 text-white rounded-lg text-[9px] font-black uppercase tracking-widest hover:bg-rose-600 transition-all disabled:opacity-50"
                  >
                    {relayTestStatus === 'TESTING' ? <Loader2 className="animate-spin" size={14} /> : 'Test Send'}
                  </button>
                </div>
                <div className="h-24 bg-slate-100 rounded-xl p-3 font-mono text-[9px] text-slate-500 overflow-y-auto custom-scrollbar">
                  {relayTestResponse || '// Ready for handshake sequence...'}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* TEMPLATE EDITOR MODAL */}
      {selectedTemplateId && templateEditForm && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200" onClick={() => setSelectedTemplateId(null)}>
          <div className="bg-white w-full max-w-4xl rounded-[3rem] border border-slate-200 shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300" onClick={e => e.stopPropagation()}>
            <div className="p-8 border-b border-slate-200 bg-white flex justify-between items-center">
              <div className="space-y-1">
                <h3 className="text-xl font-black uppercase tracking-tighter flex items-center gap-3 text-slate-900">
                  <Edit3 size={20} className="text-blue-600" />
                  Edit Communication Shard
                </h3>
                <p className="text-[10px] font-mono text-slate-500 uppercase tracking-widest">Shard ID: {selectedTemplateId}</p>
              </div>
              <button onClick={() => setSelectedTemplateId(null)} className="p-2 hover:bg-slate-100 rounded-xl"><X size={20} /></button>
            </div>

            <form onSubmit={handleSaveTemplate} className="p-8 space-y-6 bg-slate-50 max-h-[75vh] overflow-y-auto custom-scrollbar">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Shard Name</label>
                  <input
                    readOnly
                    value={templateEditForm.name || ''}
                    className="w-full bg-slate-200 border border-slate-300 rounded-2xl px-5 py-3.5 text-xs font-bold text-slate-500 cursor-not-allowed outline-none"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Subject Header</label>
                  <input
                    required
                    value={templateEditForm.subject || ''}
                    onChange={e => setTemplateEditForm({ ...templateEditForm, subject: e.target.value })}
                    placeholder="Enter email subject line..."
                    className="w-full bg-white border border-slate-200 rounded-2xl px-5 py-3.5 text-xs font-bold text-slate-900 outline-none focus:border-blue-500 transition-all shadow-sm"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex justify-between items-center px-1">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Protocol Payload (HTML Content)</label>
                  <div className="flex items-center gap-2">
                    <div className="px-2 py-1 bg-blue-500/10 text-blue-600 rounded text-[8px] font-black uppercase">Liquid Variables Enabled</div>
                  </div>
                </div>
                <textarea
                  required
                  rows={15}
                  value={templateEditForm.body || ''}
                  onChange={e => setTemplateEditForm({ ...templateEditForm, body: e.target.value })}
                  className="w-full bg-white border border-slate-200 rounded-3xl px-6 py-5 text-[11px] font-mono text-slate-800 outline-none focus:border-blue-500 transition-all shadow-sm custom-scrollbar leading-relaxed"
                />
              </div>

              <div className="flex gap-4 pt-4">
                <button
                  type="button"
                  onClick={() => setSelectedTemplateId(null)}
                  className="flex-1 py-4 bg-white border border-slate-200 text-slate-500 rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-slate-100 transition-all"
                >
                  Terminate Session
                </button>
                <button
                  type="submit"
                  className="flex-[2] py-4 bg-blue-600 text-white rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-blue-700 transition-all shadow-lg shadow-blue-600/20 flex items-center justify-center gap-2"
                >
                  <Save size={16} /> Save Protocol
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {activeTab === 'landing_media_removed' && (
        <div className="space-y-8 animate-in slide-in-from-bottom-4">
          {/* Upload Panel */}
          <div className="bg-white p-8 rounded-[2.5rem] border border-slate-200 shadow-sm space-y-6">
            <div className="flex items-center gap-3 mb-2">
              <Layout className="text-blue-500" size={24} />
              <h3 className="text-xl font-black uppercase tracking-tight text-slate-900">Upload Landing Video</h3>
            </div>

            <div className="space-y-4">
              <div className="space-y-2">
                <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">Label</label>
                <input
                  type="text"
                  value={videoUploadLabel}
                  onChange={e => setVideoUploadLabel(e.target.value)}
                  placeholder="e.g. Product Overview"
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-xs font-medium outline-none focus:border-blue-500"
                />
              </div>

              <div className="space-y-2">
                <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">Video File (MP4)</label>
                <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-slate-200 rounded-2xl cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-all">
                  <div className="text-center">
                    <Play size={24} className="text-slate-300 mx-auto mb-2" />
                    <p className="text-xs font-medium text-slate-500">
                      {videoUploadFile ? videoUploadFile.name : 'Click to select MP4'}
                    </p>
                    {videoUploadFile && (
                      <p className="text-[10px] text-slate-400 mt-1">{(videoUploadFile.size / (1024 * 1024)).toFixed(1)} MB</p>
                    )}
                  </div>
                  <input
                    type="file"
                    accept="video/mp4,video/*"
                    className="hidden"
                    onChange={e => { setVideoUploadFile(e.target.files?.[0] || null); setVideoUploadError(null); }}
                  />
                </label>
              </div>

              {videoUploadProgress !== null && (
                <div className="space-y-1">
                  <div className="w-full bg-slate-100 rounded-full h-2">
                    <div className="bg-blue-600 h-2 rounded-full transition-all" style={{ width: `${videoUploadProgress}%` }} />
                  </div>
                  <p className="text-[10px] text-slate-400 text-right">{videoUploadProgress}%</p>
                </div>
              )}

              {videoUploadError && (
                <p className="text-xs text-rose-500 font-medium">{videoUploadError}</p>
              )}

              <button
                disabled={!videoUploadFile || !videoUploadLabel.trim() || videoUploadProgress !== null}
                onClick={async () => {
                  if (!videoUploadFile || !videoUploadLabel.trim()) return;
                  setVideoUploadError(null);
                  try {
                    await firebaseService.uploadLandingVideo(videoUploadFile, videoUploadLabel.trim(), setVideoUploadProgress);
                    const updated = await firebaseService.getLandingVideos();
                    setLandingVideos(updated);
                    setVideoUploadFile(null);
                    setVideoUploadLabel('');
                    setVideoUploadProgress(null);
                  } catch (e: any) {
                    setVideoUploadError(e.message || 'Upload failed');
                    setVideoUploadProgress(null);
                  }
                }}
                className="w-full py-4 bg-blue-600 text-white rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-blue-700 transition-all shadow-lg shadow-blue-600/20 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                <Play size={14} /> Upload Video
              </button>
            </div>
          </div>

          {/* Video List */}
          <div className="bg-white p-8 rounded-[2.5rem] border border-slate-200 shadow-sm space-y-6">
            <h3 className="text-xl font-black uppercase tracking-tight text-slate-900">
              Published Videos ({landingVideos.length})
            </h3>

            {landingVideos.length === 0 ? (
              <p className="text-xs text-slate-400 text-center py-8">No videos uploaded yet.</p>
            ) : (
              <div className="flex gap-5 overflow-x-auto scroll-smooth snap-x snap-mandatory pb-4 scrollbar-hide">
                {landingVideos.map(video => (
                  <div key={video.id} className="snap-start shrink-0 w-72 rounded-2xl overflow-hidden border border-slate-200 shadow-md bg-slate-50">
                    <video
                      src={video.url}
                      muted
                      loop
                      playsInline
                      autoPlay
                      className="w-full"
                    />
                    <div className="px-4 py-3 border-t border-slate-100 flex items-center justify-between gap-2">
                      <p className="text-xs font-medium text-slate-600 truncate">{video.label}</p>
                      <button
                        onClick={async () => {
                          await firebaseService.deleteLandingVideo(video.id, video.storagePath);
                          setLandingVideos(prev => prev.filter(v => v.id !== video.id));
                        }}
                        className="shrink-0 p-1.5 text-slate-400 hover:text-rose-500 hover:bg-rose-50 rounded-lg transition-all"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};